import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { View, Text, TouchableOpacity, Image, TextInput, Modal, Alert, Platform, StyleSheet, useWindowDimensions, ScrollView } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { MediaManagerStyles as mediaManagerStyles, folderColors } from '../styles/MediaManagerStyles';
import { MediaViewer } from './MediaViewer';
import { RoomEqViewerModal } from './RoomEqViewerModal';
import * as DocumentPicker from 'expo-document-picker';
import { Ionicons } from '@expo/vector-icons';
import { getFirestore, doc, updateDoc, Timestamp, serverTimestamp, collection, query, where, getDocs, getDoc, orderBy, deleteDoc } from 'firebase/firestore';
import { getStorage, ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { getAuth } from 'firebase/auth';
import { addFileToUserUploads, getOrCreateUserUploadsFolder } from '../utils/fileManagerUtils';
import { VenueCardFile } from './VenueCardFile';
import { RoomEqSubmissionCardFile } from './RoomEqSubmissionCardFile';
import { ReportCardFile } from './ReportCardFile';
import { CommunityPostCardFile } from './CommunityPostCardFile';
import { ItemWrapper } from './ItemWrapper';
import PostGridDisplay from './PostGridDisplay';
import { buildGridFromItems, GRID_COLS, GRID_ROWS } from '../screens/CommunityPostCreate';
import { resolveCellBorderRadiusPx } from '../utils/postGridEngine';
import {
  getLinkDisplayMode,
  getLinkButtonLabel,
  getLinkButtonBg,
  getLinkButtonFg,
  getLinkButtonShape,
  getLinkButtonSize,
  getLinkButtonMetrics,
  linkButtonShapeToBorderRadius,
  LINK_DISPLAY_BUTTON,
} from '../utils/postGridLink';
import { useUser } from './LoginPersistence';
import { getResponsiveValue } from '../styles/breakpoints';
import { ITEM_TYPE_ROOM_EQ_SUBMISSION } from '../utils/profileTabMigration';
import { deriveCommunityPostFileName } from '../utils/communityPostDisplayName';
import { useTheme } from '../contexts/ThemeContext';
import { useCommunityModal } from '../contexts/CommunityModalContext';
import { applyVenueToTourFolderInUserLibrary } from '../utils/tourFolderFirestore';
import { navigate as navigateApp } from '../services/navigationService';

const POST_GRID_PREVIEW_CELL = 56;

function CommunityPostGridPreview({ items, cellSize = POST_GRID_PREVIEW_CELL }) {
    const grid = buildGridFromItems(items || []);
    const rowHeight = cellSize;
    const renderCellContent = (widget) => {
        if (!widget) return null;
        const { type, value } = widget;
        if (type === 'link' && value && getLinkDisplayMode(widget) === LINK_DISPLAY_BUTTON) {
            const m = getLinkButtonMetrics(getLinkButtonSize(widget), true);
            return (
                <View style={{ alignSelf: 'center', maxWidth: '100%', alignItems: 'center' }}>
                    <View
                        style={{
                            backgroundColor: getLinkButtonBg(widget),
                            borderRadius: linkButtonShapeToBorderRadius(getLinkButtonShape(widget)),
                            paddingHorizontal: m.padH,
                            paddingVertical: m.padV,
                        }}
                    >
                        <Text
                            style={{
                                fontFamily: 'VastagoGrotesk',
                                fontSize: m.fontSize,
                                color: getLinkButtonFg(widget),
                                fontWeight: '600',
                                textAlign: 'center',
                            }}
                            numberOfLines={2}
                        >
                            {getLinkButtonLabel(widget)}
                        </Text>
                    </View>
                </View>
            );
        }
        if (type === 'text' || type === 'link' || type === 'date') {
            return <Text style={{ fontFamily: 'VastagoGrotesk', fontSize: 10, color: '#333' }} numberOfLines={3}>{value || ''}</Text>;
        }
        if (type === 'message') return <Text style={{ fontFamily: 'VastagoGrotesk', fontSize: 9, color: '#999' }}>Message</Text>;
        if (type === 'file' && value) {
            try {
                const parsed = JSON.parse(value);
                if (parsed?.url) return <Text style={{ fontFamily: 'VastagoGrotesk', fontSize: 9, color: '#666' }} numberOfLines={1}>{parsed.name || 'File'}</Text>;
            } catch (_) {}
        }
        return <Text style={{ fontFamily: 'VastagoGrotesk', fontSize: 9, color: '#999' }} numberOfLines={2}>{value || ''}</Text>;
    };
    return (
        <View style={{ width: GRID_COLS * cellSize, alignSelf: 'center' }}>
            {Array.from({ length: GRID_ROWS }, (_, rowIndex) => (
                <View key={rowIndex} style={{ flexDirection: 'row', height: rowHeight }}>
                    {Array.from({ length: GRID_COLS }, (_, colIndex) => {
                        const cell = grid[rowIndex][colIndex];
                        if (cell?.type === 'covered') {
                            if (rowIndex === cell.item.y) return null;
                            return <View key={`c-${rowIndex}-${colIndex}`} style={{ width: cellSize, height: rowHeight, backgroundColor: 'transparent' }} />;
                        }
                        if (!cell) {
                            return <View key={`e-${rowIndex}-${colIndex}`} style={{ width: cellSize, height: rowHeight, backgroundColor: 'transparent' }} />;
                        }
                        const { item } = cell;
                        const colSpan = item.w || 1;
                        const rowSpan = item.h || 1;
                        const w = cellSize * colSpan;
                        const h = rowHeight * rowSpan;
                        const br = resolveCellBorderRadiusPx(item, w, h) ?? 0;
                        return (
                            <View key={item.id} style={{ width: w, height: h, padding: 4, borderWidth: StyleSheet.hairlineWidth, borderColor: 'rgba(0,0,0,0.1)', justifyContent: 'center', ...(br > 0 ? { borderRadius: br, overflow: 'hidden' } : {}) }}>
                                {renderCellContent(item)}
                            </View>
                        );
                    })}
                </View>
            ))}
        </View>
    );
}



const getFileType = (item) => {
    // New structure: check data.fileType first (includes 'communityPost')
    if (item.data && item.data.fileType) {
        return item.data.fileType;
    }
    // Item-level itemType for saved entities (venue, report, communityPost, communityProfile)
    if (item.itemType === 'communityPost') {
        return 'communityPost';
    }
    if (item.itemType === 'communityProfile') {
        return 'communityProfile';
    }
    if (item.itemType === 'roomNotes') {
        return 'roomNotes';
    }
    if (item.itemType === 'roomEqReference') {
        return 'roomEqReference';
    }
    if (item.itemType === ITEM_TYPE_ROOM_EQ_SUBMISSION) {
        return ITEM_TYPE_ROOM_EQ_SUBMISSION;
    }

    // Legacy structure fallback
    if (item.mediaType) {
        return item.mediaType;
    }
    
    // Fallback to file name extension if no fileType
    if (!item.name || typeof item.name !== 'string') {
        return 'file';
    }
    
    const extension = item.name.split('?')[0].split('.').pop().toLowerCase();
    
    // Image types
    if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'].includes(extension)) {
        return 'image';
    }

    // PDF
    if (extension === 'pdf') {
        return 'pdf';
    }

    // Audio types
    if (['mp3', 'wav', 'aac', 'flac', 'ogg', 'm4a', 'wma'].includes(extension)) {
        return 'audio';
    }

    // Video types
    if (['mp4', 'avi', 'mov', 'wmv', 'flv', 'webm', 'mkv'].includes(extension)) {
        return 'video';
    }

    // Document types
    if (['doc', 'docx', 'txt', 'rtf'].includes(extension)) {
        return 'document';
    }

    // Default to image for files without clear extensions
    return 'image';
};

/** True if folder or any descendant folder contains a community library item. */
function folderHasDescendantCommunityLibraryItem(folderId, folders, files) {
    for (const [, file] of Object.entries(files)) {
        if (!file || file.parentId !== folderId) continue;
        if (file.itemType === 'communityPost' || file.itemType === 'communityProfile') return true;
    }
    for (const [cid, fol] of Object.entries(folders)) {
        if (fol && fol.parentId === folderId && folderHasDescendantCommunityLibraryItem(cid, folders, files)) {
            return true;
        }
    }
    return false;
}

function isRoomNotesLibraryItem(file) {
    if (!file) return false;
    return (
        file.itemType === 'roomNotes' ||
        file.itemType === 'roomEqReference' ||
        file.itemType === ITEM_TYPE_ROOM_EQ_SUBMISSION
    );
}

/** True if folder has any non–community-post file, or a descendant folder that does. */
function folderHasDescendantVisibleInMyMedia(folderId, folders, files) {
    for (const [, file] of Object.entries(files)) {
        if (!file || file.parentId !== folderId) continue;
        if (file.itemType === 'communityPost') continue;
        if (file.itemType === 'report') continue;
        if (isRoomNotesLibraryItem(file)) continue;
        return true;
    }
    for (const [cid, fol] of Object.entries(folders)) {
        if (fol && fol.parentId === folderId && folderHasDescendantVisibleInMyMedia(cid, folders, files)) {
            return true;
        }
    }
    return false;
}

/** Folders that contain room notes shortcuts (or legacy Room EQ refs) somewhere underneath. */
function folderHasDescendantRoomEqTab(folderId, folders, files) {
    for (const [, file] of Object.entries(files)) {
        if (file && file.parentId === folderId && isRoomNotesLibraryItem(file)) return true;
    }
    for (const [cid, fol] of Object.entries(folders)) {
        if (fol && fol.parentId === folderId && folderHasDescendantRoomEqTab(cid, folders, files)) {
            return true;
        }
    }
    return false;
}

/** True if folder or any descendant folder contains a report file. */
function folderHasDescendantReport(folderId, folders, files) {
    for (const [, file] of Object.entries(files)) {
        if (file && file.parentId === folderId && file.itemType === 'report') return true;
    }
    for (const [cid, fol] of Object.entries(folders)) {
        if (fol && fol.parentId === folderId && folderHasDescendantReport(cid, folders, files)) {
            return true;
        }
    }
    return false;
}

/** Resolve Firestore tab root folder id for scoped profile library views. */
function resolveProfileTabRootFolderId(profileTabRoots, profileLibraryTab) {
    if (!profileTabRoots || profileLibraryTab === 'all') return null;
    switch (profileLibraryTab) {
        case 'media':
            return profileTabRoots.generalFolderId || null;
        case 'community':
            return profileTabRoots.communityFolderId || null;
        case 'roomEq':
            return profileTabRoots.roomNotesFolderId || null;
        case 'tours':
            return profileTabRoots.toursFolderId || null;
        case 'savedVenues':
            return profileTabRoots.savedVenuesFolderId || null;
        case 'reports':
            return profileTabRoots.reportsFolderId || null;
        default:
            return null;
    }
}

/**
 * Tour Planner stores `folder.tour` as metadata (object). Mirrored folders may use `sourceTourFolder`.
 */
function folderIsTourRelated(folder) {
    return !!(folder && (folder.tour || folder.sourceTourFolder));
}

/** True if folder is a tour folder or contains one in its subtree. */
function folderHasTourInSubtree(folderId, folders) {
    const f = folders[folderId];
    if (!f) return false;
    if (folderIsTourRelated(f)) return true;
    for (const [cid, child] of Object.entries(folders)) {
        if (child && child.parentId === folderId && folderHasTourInSubtree(cid, folders)) {
            return true;
        }
    }
    return false;
}

/** True if file lives under a tour-related folder (any ancestor). */
function fileIsUnderTourTree(file, folders) {
    if (!file?.parentId) return false;
    let cur = file.parentId;
    const seen = new Set();
    while (cur && !seen.has(cur)) {
        seen.add(cur);
        const fo = folders[cur];
        if (!fo) return false;
        if (folderIsTourRelated(fo)) return true;
        cur = fo.parentId;
    }
    return false;
}

/** Saved-venue cards that belong only in My Saved Venues (not under tour organizers or tour folders). */
function savedVenueFileBelongsInSavedVenuesTab(file, folders) {
    if (!file || file.itemType !== 'savedVenue') return false;
    if (fileIsUnderTourTree(file, folders)) return false;
    if (file.parentId && folderHasTourInSubtree(file.parentId, folders)) return false;
    return true;
}

/**
 * Has a qualifying savedVenue file here or under a subtree that is not tour-related.
 */
function folderHasSavedVenueForSavedVenuesTab(folderId, folders, files) {
    for (const [, file] of Object.entries(files)) {
        if (file && file.parentId === folderId && savedVenueFileBelongsInSavedVenuesTab(file, folders)) {
            return true;
        }
    }
    for (const [cid, child] of Object.entries(folders)) {
        if (!child || child.parentId !== folderId) continue;
        if (folderIsTourRelated(child)) continue;
        if (folderHasTourInSubtree(cid, folders)) continue;
        if (folderHasSavedVenueForSavedVenuesTab(cid, folders, files)) return true;
    }
    return false;
}


const MediaManager = ({
    userData,
    setUser,
    onEditReport,
    onDeleteReport,
    onViewVenue,
    onAddVenuePhoto,
    onDeleteVenuePhoto,
    onFolderSelect,
    venueToSave,
    communityPostToSave,
    communityProfileToSave,
    openWithCreateFolder,
    onEditRoomEqSubmission,
    /** When false: hide Add Files, block file picker uploads, folder background images, and generic media drops */
    allowFileUpload = true,
    /** When false: block Profile drag-drops for venues and reports (file drops use allowFileUpload) */
    allowProfileDropImport = true,
    /**
     * Profile tabs share one folder tree; filter listings without duplicating folders in Firestore.
     * `media` | `community` | `roomEq` | `tours` | `savedVenues` | `reports` — list from the matching `profileTabRoots.*` folder (not library root).
     * `all` — full library from parentId null.
     */
    profileLibraryTab = 'all',
    /** When true, library mutations are skipped (admin preview / compare). */
    readOnly = false,
}) => {
    const [showCreateFolder, setShowCreateFolder] = useState(false);
    const [folders, setFolders] = useState(userData?.folders || {});
    const [files, setFiles] = useState(userData?.files || {});
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
    const [deleteConfirmText, setDeleteConfirmText] = useState('');
    const [currentFolderId, setCurrentFolderId] = useState(null); // null = root level
    const [folderPath, setFolderPath] = useState([]); // Array of folder objects for breadcrumb
    const [newFolder, setNewFolder] = useState({
        name: '',
        color: '#0bb5df',
        backgroundImg: null
    });
    const [loading, setLoading] = useState(false);
    const mediaManagerRef = useRef(null);
    
    // Store navigation state in refs to prevent loss during re-renders
    const navigationStateRef = useRef({
        currentFolderId: null,
        folderPath: []
    });

    // Selection state
    const [selectedItem, setSelectedItem] = useState(null); // { id, type, item }
    const [lastTap, setLastTap] = useState(null);

    // Context menu state
    const [contextMenu, setContextMenu] = useState(null); // { item: {id, type, item}, position: {x, y} }
    const [showRenameModal, setShowRenameModal] = useState(null); // item being renamed
    const [renameText, setRenameText] = useState('');
    const [showColorModal, setShowColorModal] = useState(null); // folder being recolored
    const [selectedColor, setSelectedColor] = useState('#0bb5df');
    const [showDeleteFileModal, setShowDeleteFileModal] = useState(null); // file being deleted
    const [showSuccessModal, setShowSuccessModal] = useState(null); // success message
    const [showErrorModal, setShowErrorModal] = useState(null); // error message
    const [selectedMediaIndex, setSelectedMediaIndex] = useState(null);
    const [isMediaViewerVisible, setIsMediaViewerVisible] = useState(false);
    const [mediaViewerMedia, setMediaViewerMedia] = useState(null);
    const [notesModalVenue, setNotesModalVenue] = useState(null);
    const [notesText, setNotesText] = useState('');
    const [notesSaving, setNotesSaving] = useState(false);
    const [eqViewerVisible, setEqViewerVisible] = useState(false);
    const [eqViewerSubmissions, setEqViewerSubmissions] = useState([]);
    const [eqViewerIndex, setEqViewerIndex] = useState(0);
    const [searchQuery, setSearchQuery] = useState('');
    const [isDragOver, setIsDragOver] = useState(false);
    const [hoverDropFolderId, setHoverDropFolderId] = useState(null);
    const hoverDropFolderRef = useRef(null);
    const [showCommunityPostModal, setShowCommunityPostModal] = useState(false);
    const [communityPostModalData, setCommunityPostModalData] = useState(null);
    const [showCommunityProfileModal, setShowCommunityProfileModal] = useState(false);
    const [communityProfileModalData, setCommunityProfileModalData] = useState(null);

    const { theme } = useTheme();
    const { openMessaging } = useCommunityModal();
    const tx = theme.text.primary;
    const tq = theme.text.tertiary;

    const handleCommunityPostModalMessage = useCallback(() => {
        const post = communityPostModalData;
        if (!post) return;
        const targetId = post.postAsOrganizationId || post.userId || null;
        const targetName =
            post.postAsOrganizationName ||
            post.username ||
            post.firstName ||
            'Community Member';
        if (!targetId) {
            Alert.alert('Unavailable', 'Could not determine who to message for this post.');
            return;
        }
        const auth = getAuth();
        if (!auth.currentUser) {
            Alert.alert('Sign in', 'Please sign in to send messages.');
            return;
        }
        if (auth.currentUser.uid === targetId) {
            Alert.alert('Messaging', 'You cannot message yourself.');
            return;
        }
        if (!userData?.isCommunityMember) {
            Alert.alert('Community', 'You must be a community member to send messages.');
            return;
        }
        setShowCommunityPostModal(false);
        setCommunityPostModalData(null);
        openMessaging(targetId, targetName);
    }, [communityPostModalData, openMessaging, userData?.isCommunityMember]);

    const handleOpenCommunityProfile = useCallback(() => {
        const profile = communityProfileModalData;
        if (!profile?.profileId) {
            Alert.alert('Unavailable', 'This saved profile does not have a valid profile id.');
            return;
        }
        const isOrg = profile.entityType === 'organization';
        setShowCommunityProfileModal(false);
        setCommunityProfileModalData(null);
        if (isOrg) {
            navigateApp('OrganizationProfileScreen', { organizationId: profile.profileId });
            return;
        }
        navigateApp('UserProfileScreen', { userId: profile.profileId });
    }, [communityProfileModalData]);

    const { width: layoutWidth } = useWindowDimensions();
    /** Match folder tile proportions: smaller glyphs on narrow screens */
    const fileLibraryIconSize = useMemo(
        () =>
            getResponsiveValue({
                mobile: 22,
                tablet: 26,
                laptop: 30,
                desktop: 32,
            }),
        [layoutWidth]
    );
    const roomNoteLibraryIconSize = useMemo(
        () =>
            getResponsiveValue({
                mobile: 20,
                tablet: 24,
                laptop: 26,
                desktop: 28,
            }),
        [layoutWidth]
    );
    const filePlaceholderFontSize = useMemo(
        () =>
            getResponsiveValue({
                mobile: 8,
                tablet: 9,
                laptop: 10,
                desktop: 10,
            }),
        [layoutWidth]
    );

    const allowFileUploadRef = useRef(allowFileUpload);
    const allowProfileDropImportRef = useRef(allowProfileDropImport);
    useEffect(() => {
        allowFileUploadRef.current = allowFileUpload;
        allowProfileDropImportRef.current = allowProfileDropImport;
    }, [allowFileUpload, allowProfileDropImport]);

    /** After tab migration, each profile tab lists from its tab root folder, not library root (parentId null). */
    const profileTabRootFolderId = useMemo(
        () => resolveProfileTabRootFolderId(userData?.profileTabRoots, profileLibraryTab),
        [userData?.profileTabRoots, profileLibraryTab]
    );
    const listFolderId = useMemo(() => {
        if (profileTabRootFolderId == null) return currentFolderId;
        if (currentFolderId === null || currentFolderId === undefined) return profileTabRootFolderId;
        return currentFolderId;
    }, [profileTabRootFolderId, currentFolderId]);

    /** Profile Saved Venues tab: all saved venues in one flat list (no folders). */
    const savedVenuesFlatView = profileLibraryTab === 'savedVenues';

    /** Save-to-folder pickers list every subfolder under the tab root (incl. empty); tab views hide empty folders via descendant heuristics. */
    const relaxProfileTabFolderFilters = !!(
        onFolderSelect &&
        ((communityPostToSave && profileLibraryTab === 'community') ||
            (communityProfileToSave && profileLibraryTab === 'community') ||
            (venueToSave && profileLibraryTab === 'tours'))
    );

    const dropHandlerRef = useRef(() => {});

    // When opened with "Add to New Tour", show create folder modal
    useEffect(() => {
        if (openWithCreateFolder) {
            setShowCreateFolder(true);
        }
    }, [openWithCreateFolder]);

    // Profile (and Venue save modal) reuse one MediaManager instance when switching tabs; each tab has its own
    // root folder. Clear navigation so we don't stay "inside" a folder from another tab's tree (empty listings).
    useEffect(() => {
        setCurrentFolderId(null);
        setFolderPath([]);
        navigationStateRef.current = { currentFolderId: null, folderPath: [] };
        setSelectedItem(null);
        setLastTap(null);
        setContextMenu(null);
        setSearchQuery('');
        setHoverDropFolderId(null);
        hoverDropFolderRef.current = null;
    }, [profileLibraryTab]);

    // Set up global drop handler and drag over handler (stable wrapper → latest dropHandlerRef)
    useEffect(() => {
        const stableDrop = (data) => dropHandlerRef.current(data);
        window.mediaManagerDropHandler = stableDrop;
        window.mediaManagerDragOverHandler = (isOver) => {
            setIsDragOver(isOver);
        };
        window.mediaManagerHoverFolderRef = hoverDropFolderRef;
        window.mediaManagerElement = mediaManagerRef.current;
        return () => {
            window.mediaManagerDropHandler = null;
            window.mediaManagerDragOverHandler = null;
            window.mediaManagerHoverFolderRef = null;
            window.mediaManagerSaveVenueToFolder = null;
            window.mediaManagerElement = null;
        };
    }, []);

    // Double tap handler
    const handleItemPress = (itemId, itemType, item, onDoublePress) => {
        const now = Date.now();
        const DOUBLE_PRESS_DELAY = 300;

        if (lastTap && (now - lastTap) < DOUBLE_PRESS_DELAY && selectedItem && selectedItem.id === itemId) {
            // Double tap detected
            onDoublePress();
            setLastTap(null);
        } else {
            // Single tap
            setSelectedItem({
                id: itemId,
                type: itemType,
                item: item
            });
            setLastTap(now);
        }
    };

    // Media viewer handlers
    const handleMediaPress = (index) => {
        setMediaViewerMedia(getCurrentFolderImages());
        setSelectedMediaIndex(index);
        setIsMediaViewerVisible(true);
    };

    const handleMediaViewerClose = () => {
        setIsMediaViewerVisible(false);
        setSelectedMediaIndex(null);
        setMediaViewerMedia(null);
    };

    const handleOpenNotes = (venue) => {
        const text = userData?.venueNotes?.[venue.id]?.text || '';
        setNotesText(text);
        setNotesModalVenue(venue);
    };

    const handleCloseNotes = () => {
        setNotesModalVenue(null);
        setNotesText('');
    };

    const handleSaveNotes = async () => {
        if (!notesModalVenue || !userData?.uid) return;
        setNotesSaving(true);
        try {
            const db = getFirestore();
            const userRef = doc(db, 'users', userData.uid);
            await persistUserLibrary(userRef, {
                [`venueNotes.${notesModalVenue.id}`]: {
                    text: notesText,
                    updatedAt: serverTimestamp()
                }
            });
            setUser({ ...userData, venueNotes: { ...(userData.venueNotes || {}), [notesModalVenue.id]: { text: notesText, updatedAt: Date.now() } } });
            handleCloseNotes();
        } catch (err) {
            console.error('Save venue notes error:', err);
            setShowErrorModal(err.message || 'Failed to save notes');
        } finally {
            setNotesSaving(false);
        }
    };

    const handleOpenEq = async (venue) => {
        const auth = getAuth();
        const uid = auth.currentUser?.uid;
        if (!uid || !venue?.id) return;
        try {
            const db = getFirestore();
            const q = query(
                collection(db, `venues/${venue.id}/roomEq`),
                where('userId', '==', uid)
            );
            const snapshot = await getDocs(q);
            const submissions = [];
            snapshot.forEach((d) => {
                const data = d.data();
                if (data.type === 'roomEqSubmission') {
                    submissions.push({ id: d.id, venueId: venue.id, ...data });
                }
            });
            if (submissions.length === 0) {
                Alert.alert('No Room EQ notes', 'You have not saved any Room EQ notes for this venue yet.');
                return;
            }
            setEqViewerSubmissions(submissions);
            setEqViewerIndex(0);
            setEqViewerVisible(true);
        } catch (err) {
            console.error('Load room EQ error:', err);
            setShowErrorModal(err.message || 'Failed to load Room EQ');
        }
    };

    const handleCloseEq = () => {
        setEqViewerVisible(false);
        setEqViewerSubmissions([]);
        setEqViewerIndex(0);
    };

    const openRoomEqSubmissionViewer = async (venueId, submissionId) => {
        const auth = getAuth();
        const uid = auth.currentUser?.uid;
        if (!uid || !venueId || !submissionId) return;
        try {
            const db = getFirestore();
            const snap = await getDoc(doc(db, `venues/${venueId}/roomEq`, submissionId));
            if (!snap.exists()) {
                setShowErrorModal('This Room EQ submission could not be found.');
                return;
            }
            const data = snap.data();
            if (data.type !== 'roomEqSubmission') {
                setShowErrorModal('This document is not a Room EQ submission.');
                return;
            }
            setEqViewerSubmissions([{ id: snap.id, venueId, ...data }]);
            setEqViewerIndex(0);
            setEqViewerVisible(true);
        } catch (err) {
            console.error('openRoomEqSubmissionViewer', err);
            setShowErrorModal(err.message || 'Failed to load Room EQ');
        }
    };

    const handleGoToVenueFromEqModal = (submission) => {
        handleCloseEq();
        if (typeof onViewVenue === 'function' && submission?.venueId) {
            onViewVenue({
                id: submission.venueId,
                name: submission.venueName || 'Venue',
            });
        }
    };

    const handleDeleteEqSubmission = async (submission) => {
        try {
            const db = getFirestore();
            await deleteDoc(doc(db, `venues/${submission.venueId}/roomEq`, submission.id));
            setEqViewerSubmissions(prev => prev.filter(s => s.id !== submission.id));
            setEqViewerIndex(0);
        } catch (err) {
            console.error('Delete Room EQ submission error:', err);
            Alert.alert('Error', err.message || 'Failed to delete Room EQ entry.');
        }
    };

    const handleEditRoomEqSubmission = (submission) => {
        if (typeof onEditRoomEqSubmission === 'function') {
            onEditRoomEqSubmission(submission);
            handleCloseEq();
        }
    };

    // Get current folder's images for media viewer
    const getCurrentFolderImages = () => {
        const folderFiles = getCurrentFiles();
        const imageFiles = folderFiles.filter(([_, file]) => file.url);
        
        // Return as object with file names as keys and URLs as values (for MediaViewer compatibility)
        const imageObject = {};
        imageFiles.forEach(([fileId, file], index) => {
            imageObject[file.name || `image_${index}`] = file.url;
        });
        
        return imageObject;
    };

    // Function to fetch full report data from database
    const fetchFullReportData = async (reportId) => {
        try {
            const db = getFirestore();
            const reportRef = doc(db, 'venue_reports', reportId);
            const reportDoc = await getDoc(reportRef);
            
            if (reportDoc.exists()) {
                return reportDoc.data();
            } else {
                console.error('Report not found:', reportId);
                return null;
            }
        } catch (error) {
            console.error('Error fetching report data:', error);
            return null;
        }
    };

    const getCurrentFolderMedia = () => {
        const folderFiles = getCurrentFiles();
        const mediaFiles = folderFiles.map(([fileId, file]) => {
            const itemType = file.itemType;
            
            if (itemType === 'savedVenue') {
                return {
                    id: fileId,
                    itemType: 'savedVenue',
                    ...file.data,
                    imageUrl: file.data?.imageUrl || file.data?.url
                };
            } else if (itemType === 'report') {
                return {
                    id: fileId,
                    itemType: 'report',
                    ...file.data,
                    // Note: Full report data will be fetched when the report is opened
                    // This returns the reference data for display purposes
                };
            } else if (itemType === 'communityPost') {
                return {
                    id: fileId,
                    itemType: 'communityPost',
                    ...file.data,
                };
            } else if (itemType === 'communityProfile') {
                return {
                    id: fileId,
                    itemType: 'communityProfile',
                    ...file.data,
                };
            } else if (itemType === 'roomNotes' || itemType === 'roomEqReference') {
                return {
                    id: fileId,
                    itemType,
                    name: file.name,
                    url: file.data?.url || '',
                    type: itemType,
                    ...file.data,
                };
            } else if (itemType === ITEM_TYPE_ROOM_EQ_SUBMISSION) {
                return {
                    id: fileId,
                    itemType: ITEM_TYPE_ROOM_EQ_SUBMISSION,
                    name: file.name,
                    url: file.data?.previewImageUrl || '',
                    imageUrl: file.data?.previewImageUrl || '',
                    ...file.data,
                };
            } else {
                return {
                    id: fileId,
                    itemType: 'file',
                    name: file.name || (file.data?.url ? file.data.url.split('/').pop().split('?')[0] : 'Unknown'),
                    url: file.data?.url || file.url,
                    type: getFileType(file)
                };
            }
        });
        return mediaFiles;
    };

    const migrateExistingMedia = async () => {
        setLoading(true);
        
        try {
            const db = getFirestore();
            
            // Check if this is a bulk migration (admin) or single user migration
            const shouldMigrateAllUsers = window.confirm(
                'Migration Options:\n\n' +
                'OK = Migrate ALL users in the system (ADMIN ONLY)\n' +
                'Cancel = Migrate only current user\n\n' +
                'WARNING: Bulk migration will process ALL users and cannot be undone!'
            );
            
            await runChangePermissions(db);
            // if (shouldMigrateAllUsers) {
            // } else {
            //     await runSingleUserMigration(db);
            // }
            
        } catch (error) {
            console.error('Migration error:', error);
            Alert.alert('Migration Error', 'Failed to migrate media files. Please try again.\n\nError: ' + error.message);
        } finally {
            setLoading(false);
        }
    };


    // Bulk migration for all users
    const runBulkMigration = async (db) => {
        const usersSnapshot = await getDocs(collection(db, 'users'));
        const totalUsers = usersSnapshot.size;
        let processedUsers = 0;
        let successfulMigrations = 0;
        let failedMigrations = 0;
        let totalFilesProcessed = 0;
        const errors = [];
        
        // Process each user
        for (const userDoc of usersSnapshot.docs) {
            try {
                const currentUserData = userDoc.data();
                const currentUserId = userDoc.id;
                
                const result = await migrateSingleUser(db, currentUserId, currentUserData);
                
                if (result.success) {
                    successfulMigrations++;
                    totalFilesProcessed += result.totalFiles;
                } else {
                    failedMigrations++;
                    errors.push({
                        userId: currentUserId,
                        userEmail: currentUserData.email,
                        error: result.error
                    });
                    console.error(`❌ Failed to migrate user ${currentUserData.email || currentUserId}: ${result.error}`);
                }
                
                processedUsers++;
                
                // Small delay to avoid overwhelming the database
                await new Promise(resolve => setTimeout(resolve, 100));
                
            } catch (error) {
                failedMigrations++;
                errors.push({
                    userId: userDoc.id,
                    userEmail: userDoc.data().email,
                    error: error.message
                });
                console.error(`❌ Error processing user ${userDoc.id}:`, error);
                processedUsers++;
            }
        }

        
        
        // Show final results
        const successRate = Math.round((successfulMigrations / processedUsers) * 100);
        const summary = 
            `🎉 BULK MIGRATION COMPLETED!\n\n` +
            `📊 Results:\n` +
            `• Total Users: ${totalUsers}\n` +
            `• Processed: ${processedUsers}\n` +
            `• Successful: ${successfulMigrations}\n` +
            `• Failed: ${failedMigrations}\n` +
            `• Files Processed: ${totalFilesProcessed}\n` +
            `• Success Rate: ${successRate}%\n\n` +
            (errors.length > 0 ? `❌ ${errors.length} errors occurred (check console for details)` : '✅ No errors!');
        
        
        Alert.alert('Bulk Migration Complete', summary);
    };

    const runChangePermissions = async (db) => {
        
        const usersSnapshot = await getDocs(collection(db, 'users'));
        const totalUsers = usersSnapshot.size;
        let processedUsers = 0;
        let successfulMigrations = 0;
        let failedMigrations = 0;
        let totalFilesProcessed = 0;
        const errors = [];
        
        // Process each user
        for (const userDoc of usersSnapshot.docs) {
            try {
                const currentUserData = userDoc.data();
                const currentUserId = userDoc.id;
                
                const result = await changePermissions(db, currentUserId, currentUserData);
                
                if (result.success) {
                    successfulMigrations++;
                    totalFilesProcessed += result.totalFiles;
                } else {
                    failedMigrations++;
                    errors.push({
                        userId: currentUserId,
                        userEmail: currentUserData.email,
                        error: result.error
                    });
                    console.error(`❌ Failed to migrate user ${currentUserData.email || currentUserId}: ${result.error}`);
                }
                
                processedUsers++;
                
                // Small delay to avoid overwhelming the database
                await new Promise(resolve => setTimeout(resolve, 100));
                
            } catch (error) {
                failedMigrations++;
                errors.push({
                    userId: userDoc.id,
                    userEmail: userDoc.data().email,
                    error: error.message
                });
                console.error(`❌ Error processing user ${userDoc.id}:`, error);
                processedUsers++;
            }
        }

        
        
        // Show final results
        const successRate = Math.round((successfulMigrations / processedUsers) * 100);
        const summary = 
            `🎉 BULK MIGRATION COMPLETED!\n\n` +
            `📊 Results:\n` +
            `• Total Users: ${totalUsers}\n` +
            `• Processed: ${processedUsers}\n` +
            `• Successful: ${successfulMigrations}\n` +
            `• Failed: ${failedMigrations}\n` +
            `• Files Processed: ${totalFilesProcessed}\n` +
            `• Success Rate: ${successRate}%\n\n` +
            (errors.length > 0 ? `❌ ${errors.length} errors occurred (check console for details)` : '✅ No errors!');
        
        Alert.alert('Bulk Migration Complete', summary);
    };

    // Single user migration
    const runSingleUserMigration = async (db) => {
        
        const result = await migrateSingleUser(db, userData.uid, userData);
        
        if (result.success && result.totalFiles > 0) {
            Alert.alert(
                'Migration Complete!', 
                `Successfully migrated ${result.totalFiles} files.\n\n` +
                `• ${result.profileFiles} profile media files moved to root\n` +
                `• ${result.reportFiles} report files moved to User Uploads folder\n` +
                `• ${result.venueImageFiles} venue images moved to User Uploads folder`
            );
            
            // Update local state for current user
            if (result.updatedFolders) setFolders(result.updatedFolders);
            if (result.updatedFiles) setFiles(result.updatedFiles);
            
        } else if (result.success) {
            Alert.alert('Migration Complete', 'No files found that needed migration.');
        } else {
            throw new Error(result.error);
        }
    };

    const changePermissions = async (db, userId, currentUserData) => {
        try {
            const userRef = doc(db, 'users', userId);
            const existingFiles = currentUserData.files || {};

            // Change permissions for all files to make them non-protected
            let filesProcessed = 0;
            for (const fileId in existingFiles) {
                const file = existingFiles[fileId];
                if (file.protected === true) {
                file.protected = false;
                    filesProcessed++;
                }
            }

            // Only update if there were files to process
            if (filesProcessed > 0) {
            await persistUserLibrary(userRef, {
                files: existingFiles
            });
            }

            return {
                success: true,
                totalFiles: filesProcessed
            };

        } catch (error) {
            return {
                success: false,
                error: error.message
            };
        }
    };


    // Reset all media data for all users (DANGEROUS - ADMIN ONLY)
    const resetAllMediaData = async () => {
        // Multiple confirmation dialogs to prevent accidental execution
 
        
        setLoading(true);
        
        try {
            const db = getFirestore();
            const storage = getStorage();
            const usersSnapshot = await getDocs(collection(db, 'users'));
            const totalUsers = usersSnapshot.size;
            let processedUsers = 0;
            let successfulResets = 0;
            let failedResets = 0;
            let totalFilesDeleted = 0;
            let totalFoldersDeleted = 0;
            const errors = [];
            
            // Process each user
            for (const userDoc of usersSnapshot.docs) {
                try {
                    const currentUserData = userDoc.data();
                    const currentUserId = userDoc.id;
                    
                    const existingFolders = currentUserData.folders || {};
                    const existingFiles = currentUserData.files || {};
                    
                    // Keep only protected folders
                    const protectedFolders = {};
                    const foldersToDelete = [];
                    
                    // Delete all files from storage and database
                    let userFilesDeleted = 0;
                    const filesToDelete = Object.entries(existingFiles);
                    
                    // Prepare update data
                    const updateData = {
                        folders: protectedFolders,
                        files: {}, // Clear all files
                        migrationCompleted: false,
                        migrationDate: null
                    };
                    
                    // Update database
                        const userRef = doc(db, 'users', currentUserId);
                        await persistUserLibrary(userRef, updateData);
                    
                    totalFilesDeleted += userFilesDeleted;
                    totalFoldersDeleted += foldersToDelete.length;
                    successfulResets++;
                    processedUsers++;
                    
                    // Small delay to avoid overwhelming the database
                    await new Promise(resolve => setTimeout(resolve, 100));
                    
                } catch (error) {
                    failedResets++;
                    errors.push({
                        userId: userDoc.id,
                        userEmail: userDoc.data().email,
                        error: error.message
                    });
                    console.error(`❌ Error resetting user ${userDoc.id}:`, error);
                    processedUsers++;
                }
            }
            
            // Show final results
            const successRate = Math.round((successfulResets / processedUsers) * 100);
            const summary = 
                `🚨 RESET OPERATION COMPLETED!\n\n` +
                `📊 Results:\n` +
                `• Total Users: ${totalUsers}\n` +
                `• Processed: ${processedUsers}\n` +
                `• Successful: ${successfulResets}\n` +
                `• Failed: ${failedResets}\n` +
                `• Files Deleted: ${totalFilesDeleted}\n` +
                `• Folders Deleted: ${totalFoldersDeleted}\n` +
                `• Success Rate: ${successRate}%\n\n` +
                `✅ Only protected folders remain!\n` +
                `✅ All files have been deleted from storage!\n\n` +
                (errors.length > 0 ? `❌ ${errors.length} errors occurred (check console for details)` : '✅ No errors!');
            
            
            // Update local state for current user
            setFolders({});
            setFiles({});
            
            setShowSuccessModal(summary);
            
        } catch (error) {
            console.error('❌ Reset operation error:', error);
            setShowErrorModal(`Failed to reset media data: ${error.message}\n\nCheck the console for more details.`);
        } finally {
            setLoading(false);
        }
    };

    // Set all files to protected = false for all users
    const setAllFilesUnprotected = async () => {
        const confirm = window.confirm(
            '⚠️ Set All Files to Unprotected ⚠️\n\n' +
            'This will set protected = false for ALL files in ALL users\' media managers.\n\n' +
            '• This will make all files deletable\n' +
            '• Only folders with protected = true will remain protected\n' +
            '• This affects ALL users in the system\n\n' +
            'Are you sure you want to continue?'
        );
        
        if (!confirm) return;
        
        setLoading(true);
        
        try {
            const db = getFirestore();
            const usersSnapshot = await getDocs(collection(db, 'users'));
            const totalUsers = usersSnapshot.size;
            let processedUsers = 0;
            let successfulUpdates = 0;
            let failedUpdates = 0;
            let totalFilesUpdated = 0;
            const errors = [];
            
            // Process each user
            for (const userDoc of usersSnapshot.docs) {
                try {
                    const currentUserData = userDoc.data();
                    const currentUserId = userDoc.id;
                    
                    const existingFiles = currentUserData.files || {};
                    const filesToUpdate = {};
                    let userFilesUpdated = 0;
                    
                    // Update each file to set protected = false
                    Object.entries(existingFiles).forEach(([fileId, file]) => {
                        filesToUpdate[fileId] = {
                            ...file,
                            protected: false
                        };
                        userFilesUpdated++;
                    });
                    
                    // Update database if there are files to update
                    if (userFilesUpdated > 0) {
                        const userRef = doc(db, 'users', currentUserId);
                        await persistUserLibrary(userRef, { files: filesToUpdate });
                    }
                    
                    totalFilesUpdated += userFilesUpdated;
                    successfulUpdates++;
                    processedUsers++;
                    
                    // Small delay to avoid overwhelming the database
                    await new Promise(resolve => setTimeout(resolve, 100));
                    
                } catch (error) {
                    failedUpdates++;
                    errors.push({
                        userId: userDoc.id,
                        userEmail: userDoc.data().email,
                        error: error.message
                    });
                    console.error(`❌ Error updating user ${userDoc.id}:`, error);
                    processedUsers++;
                }
            }
            
            // Show final results
            const successRate = totalUsers > 0 ? ((successfulUpdates / totalUsers) * 100).toFixed(1) : 0;
            const summary = `✅ Operation Complete!\n\n` +
                `📊 Results:\n` +
                `• Total users processed: ${processedUsers}\n` +
                `• Successful updates: ${successfulUpdates}\n` +
                `• Failed updates: ${failedUpdates}\n` +
                `• Total files updated: ${totalFilesUpdated}\n` +
                `• Success rate: ${successRate}%\n\n` +
                `All files are now unprotected and can be deleted.`;
            
            
            // Update local state for current user
            const updatedFiles = {};
            Object.entries(files).forEach(([fileId, file]) => {
                updatedFiles[fileId] = {
                    ...file,
                    protected: false
                };
            });
            setFiles(updatedFiles);
            
            setShowSuccessModal(summary);
            
        } catch (error) {
            console.error('❌ Update operation error:', error);
            setShowErrorModal(`Failed to update files: ${error.message}\n\nCheck the console for more details.`);
        } finally {
            setLoading(false);
        }
    };

    // Helper function to determine target folder for migration
    const getTargetFolderForMigration = async () => {
        // If hovering a folder tile, prefer that as the target
        if (hoverDropFolderRef.current) return hoverDropFolderRef.current;
        if (hoverDropFolderId) return hoverDropFolderId;
        // Use ref as primary source, fallback to state
        const refState = navigationStateRef.current;
        let effectiveCurrentFolderId = refState.currentFolderId || currentFolderId;
        let effectiveFolderPath = refState.folderPath.length > 0 ? refState.folderPath : folderPath;
        // Backup check: if effectiveCurrentFolderId is null but we have a folderPath, use the last folder in the path
        if (effectiveCurrentFolderId === null && effectiveFolderPath.length > 0) {
            const lastFolderInPath = effectiveFolderPath[effectiveFolderPath.length - 1];
            effectiveCurrentFolderId = lastFolderInPath.id;
        }

        if (effectiveCurrentFolderId === null || effectiveCurrentFolderId === undefined) {
            if (profileTabRootFolderId) return profileTabRootFolderId;
            return null;
        }
        
        // Fetch the latest folder data from the database to ensure we have the most up-to-date information
        let currentFolders = userData?.folders || folders;
        
        // If the folder doesn't exist in our current data, fetch from database
        if (!currentFolders[effectiveCurrentFolderId]) {
            try {
                const userRef = doc(db, 'users', userData.uid);
                const userDoc = await getDoc(userRef);
                const latestUserData = userDoc.data();
                currentFolders = latestUserData.folders || currentFolders;
            } catch (error) {
                console.error('Error fetching latest folder data:', error);
                // Fall back to current data
            }
        }
        
        // Check if current folder is within User Uploads folder hierarchy
        const isWithinUserUploads = (folderId) => {
            if (!folderId) return false;
            
            const folder = currentFolders[folderId];
            if (!folder) return false;
            
            // Check if this folder is the User Uploads folder itself
            if (folder.protected === true && folder.name === 'User Uploads') {
                return true;
            }
            
            // Check if this folder is a child of User Uploads
            if (folder.parentId) {
                return isWithinUserUploads(folder.parentId);
            }
            
            return false;
        };
        
        // Use the current folder if available; otherwise fallback to User Uploads
        // This allows drops into any open folder (not only within User Uploads)
        if (effectiveCurrentFolderId) {
            return effectiveCurrentFolderId;
        }
        
        // Root (no folder open) → caller will fallback to User Uploads
        return null;
    };

    // Add venue to Media Manager as a complete entity
    const addVenueToMediaManager = async (venueData) => {
        try {
            setLoading(true);
            const db = getFirestore();
            const auth = getAuth();
            const userId = auth.currentUser?.uid;
            
            if (!userId) {
                Alert.alert('Error', 'User not authenticated');
                return;
            }

            // Get current user data from database to ensure we have the latest state
            const userRef = doc(db, 'users', userId);
            const userDoc = await getDoc(userRef);
            const userDataLatest = userDoc.data();
            const currentFiles = userDataLatest?.files || {};
            const currentFolders = userDataLatest?.folders || {};

            // Determine target folder
            const targetFolderId = await getTargetFolderForMigration();
            let userUploadsFolderId;
            
            if (targetFolderId) {
                userUploadsFolderId = targetFolderId;
            } else {
                userUploadsFolderId = await getOrCreateUserUploadsFolder(userId);
            }


            const venueId = venueData.id;
            const savedVenueImage = userData.savedVenueImages ? userData.savedVenueImages[venueId] : null;
            

            
            try {
                // Get current user data from database to ensure we have the latest state
                
                // Create venue entry in files using new structure
                const fileId = `item_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
                const venueFileData = {
                    name: venueData.name,
                    id: fileId,
                    parentId: userUploadsFolderId,
                    itemType: 'savedVenue',
                    data: {
                        id: venueData.id || venueId,
                        name: venueData.name || '',
                        city: venueData.city || '',
                        state: venueData.state || '',
                        capacity: venueData.capacity || '',
                        imageUrl: savedVenueImage || '',
                        url: savedVenueImage || ''  
                    },
                    order: Object.values(currentFiles).filter(f => f.parentId === userUploadsFolderId).length,
                    createdAt: Timestamp.now(),
                    updatedAt: Timestamp.now()
                };
                
                
                const userRef = doc(db, 'users', userId);
                const userDoc = await getDoc(userRef);
                const currentUserData = userDoc.data();
                const updatedFiles = { ...currentUserData.files, [fileId]: venueFileData };
                await persistUserLibrary(userRef, { files: updatedFiles });
                setFiles(updatedFiles);

                const updatedFolders = currentUserData.folders || {};
                setFolders(updatedFolders);

                setUser({
                    ...userData,
                    files: updatedFiles,
                    folders: updatedFolders
                });

                
            } catch (error) {
                console.error('Error adding venue to Media Manager:', error);
                Alert.alert('Error', `Failed to add venue: ${error.message}`);
            }
            
        } catch (error) {
            console.error('Error adding venue to Media Manager:', error);
            Alert.alert('Error', `Failed to add venue: ${error.message}`);
        } finally {
            setLoading(false);
        }
    };

    // Function to save venue to a specific folder
    const saveVenueToFolder = async (folderId, venueData) => {
        if (readOnly) return;
        // Temporarily set the target folder (used by getTargetFolderForMigration for non-tour saves)
        const originalFolderId = currentFolderId;
        setCurrentFolderId(folderId);
        navigationStateRef.current.currentFolderId = folderId;
        hoverDropFolderRef.current = folderId;

        try {
            if (profileLibraryTab === 'tours') {
                setLoading(true);
                const dbf = getFirestore();
                const userRef = doc(dbf, 'users', userData.uid);
                const { folders: nextFolders, files: nextFiles } = await applyVenueToTourFolderInUserLibrary(
                    userRef,
                    folderId,
                    venueData
                );
                setFolders(nextFolders);
                setFiles(nextFiles);
                setUser({ ...userData, folders: nextFolders, files: nextFiles });
            } else {
                await addVenueToMediaManager(venueData);
            }
        } catch (e) {
            console.error('saveVenueToFolder', e);
            Alert.alert('Error', e?.message || 'Failed to save venue to folder');
        } finally {
            if (profileLibraryTab === 'tours') {
                setLoading(false);
            }
            setCurrentFolderId(originalFolderId);
            navigationStateRef.current.currentFolderId = originalFolderId;
            hoverDropFolderRef.current = null;
        }
    };

    // Add community post to Media Manager
    const addCommunityPostToMediaManager = async (postData, explicitTargetFolderId) => {
        try {
            setLoading(true);
            const db = getFirestore();
            const auth = getAuth();
            const userId = auth.currentUser?.uid;
            if (!userId) {
                Alert.alert('Error', 'User not authenticated');
                return;
            }
            const userRef = doc(db, 'users', userId);
            const userDoc = await getDoc(userRef);
            const currentUserData = userDoc.data() || {};
            const currentFiles = currentUserData.files || {};
            const currentFolders = currentUserData.folders || {};
            const targetFolderId =
                explicitTargetFolderId != null && explicitTargetFolderId !== ''
                    ? explicitTargetFolderId
                    : listFolderId || (await getOrCreateUserUploadsFolder(userId));
            const fileId = `item_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
            const postName = deriveCommunityPostFileName(postData);
            const postFileData = {
                name: postName,
                id: fileId,
                parentId: targetFolderId,
                itemType: 'communityPost',
                data: {
                    fileType: 'communityPost',
                    postId: postData.id,
                    userId: postData.userId,
                    username: postData.username || null,
                    photoURL: postData.photoURL || null,
                    firstName: postData.firstName || null,
                    lastName: postData.lastName || null,
                    postTitle: postName,
                    postContent: postData.postContent || '',
                    location: postData.location || '',
                    items: postData.items || [],
                    postSelected: postData.postSelected || {},
                    postCustom: postData.postCustom || {},
                    createdAt: postData.createdAt || null,
                },
                order: Object.values(currentFiles).filter(f => f.parentId === targetFolderId).length,
                createdAt: Timestamp.now(),
                updatedAt: Timestamp.now(),
            };
            const updatedFiles = { ...currentFiles, [fileId]: postFileData };
            await persistUserLibrary(userRef, { files: updatedFiles });
            setFiles(updatedFiles);
            setUser({ ...userData, files: updatedFiles, folders: currentFolders });
            setShowSuccessModal('Post saved to folder.');
        } catch (error) {
            console.error('Error adding community post to Media Manager:', error);
            Alert.alert('Error', `Failed to save post: ${error.message}`);
        } finally {
            setLoading(false);
        }
    };

    const saveCommunityPostToFolder = async (folderId, postData) => {
        const originalFolderId = currentFolderId;
        setCurrentFolderId(folderId);
        navigationStateRef.current.currentFolderId = folderId;
        hoverDropFolderRef.current = folderId;
        try {
            await addCommunityPostToMediaManager(postData, folderId);
        } finally {
            setCurrentFolderId(originalFolderId);
            navigationStateRef.current.currentFolderId = originalFolderId;
            hoverDropFolderRef.current = null;
        }
    };

    const addCommunityProfileToMediaManager = async (profileData, explicitTargetFolderId) => {
        try {
            setLoading(true);
            const db = getFirestore();
            const auth = getAuth();
            const userId = auth.currentUser?.uid;
            if (!userId) {
                Alert.alert('Error', 'User not authenticated');
                return;
            }
            const userRef = doc(db, 'users', userId);
            const userDoc = await getDoc(userRef);
            const currentUserData = userDoc.data() || {};
            const currentFiles = currentUserData.files || {};
            const currentFolders = currentUserData.folders || {};
            const targetFolderId =
                explicitTargetFolderId != null && explicitTargetFolderId !== ''
                    ? explicitTargetFolderId
                    : listFolderId || (await getOrCreateUserUploadsFolder(userId));
            const fileId = `item_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
            const profileName =
                (profileData?.username && String(profileData.username).trim()) ||
                (profileData?.title && String(profileData.title).trim()) ||
                'Community profile';
            const profileFileData = {
                name: profileName,
                id: fileId,
                parentId: targetFolderId,
                itemType: 'communityProfile',
                data: {
                    fileType: 'communityProfile',
                    profileId: profileData?.id || null,
                    entityType: profileData?.entityType || 'user',
                    username: profileData?.username || null,
                    firstName: profileData?.firstName || null,
                    lastName: profileData?.lastName || null,
                    title: profileData?.title || null,
                    location: profileData?.location || null,
                    bio: profileData?.bio || null,
                    photoURL: profileData?.photoURL || null,
                    accountType: profileData?.accountType || null,
                    createdAt: profileData?.createdAt || null,
                },
                order: Object.values(currentFiles).filter(f => f.parentId === targetFolderId).length,
                createdAt: Timestamp.now(),
                updatedAt: Timestamp.now(),
            };
            const updatedFiles = { ...currentFiles, [fileId]: profileFileData };
            await persistUserLibrary(userRef, { files: updatedFiles });
            setFiles(updatedFiles);
            setUser({ ...userData, files: updatedFiles, folders: currentFolders });
            setShowSuccessModal('Profile saved to folder.');
        } catch (error) {
            console.error('Error adding community profile to Media Manager:', error);
            Alert.alert('Error', `Failed to save profile: ${error.message}`);
        } finally {
            setLoading(false);
        }
    };

    const saveCommunityProfileToFolder = async (folderId, profileData) => {
        const originalFolderId = currentFolderId;
        setCurrentFolderId(folderId);
        navigationStateRef.current.currentFolderId = folderId;
        hoverDropFolderRef.current = folderId;
        try {
            await addCommunityProfileToMediaManager(profileData, folderId);
        } finally {
            setCurrentFolderId(originalFolderId);
            navigationStateRef.current.currentFolderId = originalFolderId;
            hoverDropFolderRef.current = null;
        }
    };

    // Add report to Media Manager as a complete entity
    const addReportToMediaManager = async (reportData) => {
        try {
            setLoading(true);
            const db = getFirestore();
            const auth = getAuth();
            const userId = auth.currentUser?.uid;
            
            if (!userId) {
                Alert.alert('Error', 'User not authenticated');
                return;
            }

            // Get current user data from database to ensure we have the latest state
            const userRef = doc(db, 'users', userId);
            const userDoc = await getDoc(userRef);
            const userDataLatest = userDoc.data();
            const currentFiles = userDataLatest?.files || {};

            // Determine target folder
            const targetFolderId = await getTargetFolderForMigration();
            let userUploadsFolderId;
            
            if (targetFolderId) {
                userUploadsFolderId = targetFolderId;
            } else {
                userUploadsFolderId = await getOrCreateUserUploadsFolder(userId);
            }

            try {
                // Fetch the latest user files/folders to avoid overwriting
                const userRef = doc(db, 'users', userId);
                const userDocSnap = await getDoc(userRef);
                const latestUserData = userDocSnap.data() || {};
                const currentFiles = latestUserData.files || {};
                const currentFolders = latestUserData.folders || {};

                const venueName = reportData.venueName || reportData.venue?.name || 'Report';
                const firstPhoto = (reportData.photos && reportData.photos[0]) || '';

                // Create report entry in files using new structure
                const fileId = `item_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
                const reportFileData = {
                    name: `${venueName} - Report`,
                    id: fileId,
                    parentId: userUploadsFolderId,
                    itemType: 'report',
                    data: {
                        reportId: reportData.id,
                        venueId: reportData.venueId,
                        venueName,
                        url: firstPhoto,
                        imageUrl: firstPhoto,
                        hasPhotos: Array.isArray(reportData.photos) && reportData.photos.length > 0,
                        hasVideos: Array.isArray(reportData.videos) && reportData.videos.length > 0,
                        hasDocuments: Array.isArray(reportData.documents) && reportData.documents.length > 0,
                        status: reportData.status || 'pending',
                        userType: reportData.userType,
                        isTechSpecsOnly: !!reportData.isTechSpecsOnly,
                        createdAt: reportData.createdAt || Timestamp.now(),
                        dateOfPerformance: reportData.dateOfPerformance,
                        bandName: reportData.bandName,
                        datesWorked: reportData.datesWorked,
                        jobTitle: reportData.jobTitle,
                        notes: reportData.notes
                    },
                    order: Object.values(currentFiles).filter(f => f.parentId === userUploadsFolderId).length,
                    createdAt: Timestamp.now(),
                    updatedAt: Timestamp.now()
                };


                const updatedFiles = { ...currentFiles, [fileId]: reportFileData };

                await persistUserLibrary(userRef, { files: updatedFiles });

                // Refresh local state with latest snapshot
                const refreshedUserDoc = await getDoc(userRef);
                const refreshedData = refreshedUserDoc.data() || {};
                setFiles(refreshedData.files || updatedFiles);
                setFolders(refreshedData.folders || currentFolders);
                setUser({ ...userData, files: refreshedData.files || updatedFiles, folders: refreshedData.folders || currentFolders });

                const targetFolderName = targetFolderId ? (currentFolders[targetFolderId]?.name || 'current folder') : 'User Uploads folder';
                Alert.alert('Report Added!', `Successfully added report for "${venueName}" to ${targetFolderName}.`);
            } catch (error) {
                console.error('Error adding report to Media Manager:', error);
                Alert.alert('Error', `Failed to add report: ${error.message}`);
            }
            
        } catch (error) {
            console.error('Error adding report to Media Manager:', error);
            Alert.alert('Error', `Failed to add report: ${error.message}`);
        } finally {
            setLoading(false);
        }
    };

    // Add generic media (image/pdf/audio/video) to Media Manager
    const addMediaToMediaManager = async (mediaData) => {
        if (!allowFileUploadRef.current) {
            return;
        }
        try {
            setLoading(true);
            const db = getFirestore();
            const auth = getAuth();
            const userId = auth.currentUser?.uid;

            if (!userId) {
                Alert.alert('Error', 'User not authenticated');
                return;
            }

            // Determine target folder (hovered folder preferred)
            const targetFolderId = await getTargetFolderForMigration();
            const destinationFolderId = targetFolderId || await getOrCreateUserUploadsFolder(userId);

            // Fetch latest user files to compute order safely
            const userRef = doc(db, 'users', userId);
            const userSnap = await getDoc(userRef);
            const latestData = userSnap.data() || {};
            const currentFiles = latestData.files || {};
            const currentFolders = latestData.folders || {};

            const name = mediaData.name || mediaData.fileName || 'media';
            const url = mediaData.url || mediaData.downloadURL || '';

            // Infer fileType from extension
            const ext = (name.split('?')[0].split('.').pop() || '').toLowerCase();
            let fileType = 'image';
            if (['pdf'].includes(ext)) fileType = 'pdf';
            else if (['mp3', 'wav', 'aac', 'flac', 'ogg', 'm4a', 'wma'].includes(ext)) fileType = 'audio';
            else if (['mp4', 'avi', 'mov', 'wmv', 'flv', 'webm', 'mkv'].includes(ext)) fileType = 'video';
            else if (['doc', 'docx', 'txt', 'rtf'].includes(ext)) fileType = 'document';

            const fileId = `item_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
            const newFile = {
                id: fileId,
                name,
                parentId: destinationFolderId,
                itemType: 'file',
                data: {
                    url,
                    fileType
                },
                order: Object.values(currentFiles).filter(f => f.parentId === destinationFolderId).length,
                createdAt: Timestamp.now(),
                updatedAt: Timestamp.now()
            };

            const updatedFiles = { ...currentFiles, [fileId]: newFile };
            await persistUserLibrary(userRef, { files: updatedFiles });

            const refreshed = await getDoc(userRef);
            const refreshedData = refreshed.data() || {};
            setFiles(refreshedData.files || updatedFiles);
            setFolders(refreshedData.folders || currentFolders);
            setUser({ ...userData, files: refreshedData.files || updatedFiles, folders: refreshedData.folders || currentFolders });

            const targetFolderName = targetFolderId ? (currentFolders[targetFolderId]?.name || 'current folder') : 'User Uploads folder';
            Alert.alert('Media Added!', `Added "${name}" to ${targetFolderName}.`);
        } catch (error) {
            console.error('Error adding media to Media Manager:', error);
            Alert.alert('Error', `Failed to add media: ${error.message}`);
        } finally {
            setLoading(false);
        }
    };

    // Migrate venue media to User Uploads folder
    const migrateSavedVenueMedia = async (venueData) => {
        try {
            setLoading(true);
            const db = getFirestore();
            const auth = getAuth();
            const userId = auth.currentUser?.uid;
            
            if (!userId) {
                Alert.alert('Error', 'User not authenticated');
                return;
            }

            
            // Determine target folder
            const targetFolderId = await getTargetFolderForMigration();
            let userUploadsFolderId;
            
            if (targetFolderId) {
                // Use the current folder if it's within User Uploads hierarchy
                userUploadsFolderId = targetFolderId;
            } else {
                // Get or create User Uploads folder
                userUploadsFolderId = await getOrCreateUserUploadsFolder(userId);
            }

            const venueId = venueData.id;
            const savedVenueImage = userData.savedVenueImages ? userData.savedVenueImages[venueId] : null;
            
            if (!savedVenueImage) {
                Alert.alert('No Image', 'This saved venue has no image to migrate.');
                return;
            }
            
            try {
                // Generate filename
                const fileName = `${venueData.name}_saved_venue_photo`;
                
                // Add to target folder (unprotected)
                await addFileToUserUploads(userId, fileName, savedVenueImage, 'saved_venue_image', {
                    venueId: venueData.id,
                    venueName: venueData.name,
                    mediaType: 'photos',
                    savedVenueId: venueData.id,
                    savedVenueName: venueData.name,
                    migrationDate: new Date().toISOString(),
                    targetFolderId: userUploadsFolderId // Pass the target folder ID
                });
                
            } catch (error) {
            }
    
            
            // Update local state
            const userRef = doc(db, 'users', userId);
            const userDoc = await getDoc(userRef);
            if (userDoc.exists()) {
                const userData = userDoc.data();
                setFolders(userData.folders || {});
                setFiles(userData.files || {});
            }
            
            const targetFolderName = targetFolderId ? folders[targetFolderId]?.name || 'current folder' : 'User Uploads folder';
            Alert.alert(
                'Venue Media Migrated!', 
                `Successfully migrated saved venue photo for "${venueData.name}" to ${targetFolderName}.`
            );
            
        } catch (error) {
            console.error('Error migrating venue media:', error);
            Alert.alert('Error', `Failed to migrate venue media: ${error.message}`);
        } finally {
            setLoading(false);
        }
    };

    // Migrate report media to User Uploads folder
    const migrateReportMedia = async (reportData) => {
        try {
            setLoading(true);
            const db = getFirestore();
            const auth = getAuth();
            const userId = auth.currentUser?.uid;
            
            if (!userId) {
                Alert.alert('Error', 'User not authenticated');
                return;
            }

            
            // Determine target folder
            const targetFolderId = await getTargetFolderForMigration();
            let userUploadsFolderId;
            
            if (targetFolderId) {
                // Use the current folder if it's within User Uploads hierarchy
                userUploadsFolderId = targetFolderId;
            } else {
                // Get or create User Uploads folder
                userUploadsFolderId = await getOrCreateUserUploadsFolder(userId);
            }
            
            let migratedCount = 0;
            const mediaTypes = ['photos', 'videos', 'documents'];
            
            // Process each media type
            for (const mediaType of mediaTypes) {
                const mediaArray = reportData[mediaType] || [];
                
                for (let i = 0; i < mediaArray.length; i++) {
                    const mediaUrl = mediaArray[i];
                    if (mediaUrl && typeof mediaUrl === 'string') {
                        try {
                            // Generate filename
                            const fileName = `${reportData.venueName}_report_${mediaType.slice(0, -1)}_${i + 1}`;
                            
                            // Add to target folder (unprotected)
                            await addFileToUserUploads(userId, fileName, mediaUrl, 'report_migration', {
                                reportId: reportData.id,
                                venueId: reportData.venueId,
                                venueName: reportData.venueName,
                                mediaType: mediaType,
                                originalIndex: i,
                                migrationDate: new Date().toISOString(),
                                targetFolderId: userUploadsFolderId // Pass the target folder ID
                            });
                            
                            migratedCount++;
                        } catch (error) {
                        }
                    }
                }
            }
            
            // Update local state
            const userRef = doc(db, 'users', userId);
            const userDoc = await getDoc(userRef);
            if (userDoc.exists()) {
                const userData = userDoc.data();
                setFolders(userData.folders || {});
                setFiles(userData.files || {});
            }
            
            const targetFolderName = targetFolderId ? folders[targetFolderId]?.name || 'current folder' : 'User Uploads folder';
            Alert.alert(
                'Report Media Migrated!', 
                `Successfully migrated ${migratedCount} media files from report for "${reportData.venueName}" to ${targetFolderName}.`
            );
            
        } catch (error) {
            console.error('Error migrating report media:', error);
            Alert.alert('Error', `Failed to migrate report media: ${error.message}`);
        } finally {
            setLoading(false);
        }
    };

    // Check if migration is needed

    // Update local state when userData changes
    useEffect(() => {
        setFolders(userData?.folders || {});
        setFiles(userData?.files || {});
    }, [userData]);

    // Preserve navigation state when folders change
    useEffect(() => {
        // If we're currently in a folder that no longer exists, navigate back to root
        if (currentFolderId && !folders[currentFolderId]) {
            setCurrentFolderId(null);
            setFolderPath([]);
        }
    }, [folders, currentFolderId]);

    const db = getFirestore();
    const storage = getStorage();

    const persistUserLibrary = async (ref, payload) => {
        if (readOnly) return;
        await updateDoc(ref, payload);
    };



    // Detect if user has a mouse/cursor (desktop) or touch (mobile)
    const hasMouseSupport = () => {
        if (Platform.OS !== 'web') return false;

        // Check if the device has a mouse
        return window.matchMedia('(pointer: fine)').matches;
    };

    // Check if device supports touch
    const hasTouchSupport = () => {
        if (Platform.OS === 'ios' || Platform.OS === 'android') return true;

        if (Platform.OS === 'web') {
            return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
        }

        return false;
    };


    // Navigation helper functions
    const navigateToFolder = (folderId) => {
        if (folderId) {
            const folder = folders[folderId];
            if (folder) {
                const newFolderPath = [...folderPath, { id: folderId, name: folder.name }];
                
                // Update state
                setCurrentFolderId(folderId);
                setFolderPath(newFolderPath);
                
                // Update ref
                navigationStateRef.current = {
                    currentFolderId: folderId,
                    folderPath: newFolderPath
                };
            }
        } else {
            // Update state
            setCurrentFolderId(null);
            setFolderPath([]);
            
            // Update ref
            navigationStateRef.current = {
                currentFolderId: null,
                folderPath: []
            };
        }
    };

    const navigateBack = () => {
        if (folderPath.length > 0) {
            const newPath = [...folderPath];
            newPath.pop();
            const newCurrentFolderId = newPath.length > 0 ? newPath[newPath.length - 1].id : null;
            
            setFolderPath(newPath);
            setCurrentFolderId(newCurrentFolderId);
            
            // Update ref
            navigationStateRef.current = {
                currentFolderId: newCurrentFolderId,
                folderPath: newPath
            };
        }
    };

    const navigateToBreadcrumb = (index) => {
        if (index === -1) {
            // Navigate to root
            setCurrentFolderId(null);
            setFolderPath([]);
            
            // Update ref
            navigationStateRef.current = {
                currentFolderId: null,
                folderPath: []
            };
        } else {
            const newPath = folderPath.slice(0, index + 1);
            const newCurrentFolderId = newPath[newPath.length - 1].id;
            
            setFolderPath(newPath);
            setCurrentFolderId(newCurrentFolderId);
            
            // Update ref
            navigationStateRef.current = {
                currentFolderId: newCurrentFolderId,
                folderPath: newPath
            };
        }
    };

    // Get folders in current directory
    const getCurrentFolders = () => {
        const foldersMap = userData?.folders || {};
        const filesMap = userData?.files || {};
        // Saved Venues is a single flat folder; no subfolders are shown.
        if (savedVenuesFlatView) return [];
        let entries = Object.entries(foldersMap).filter(([_, folder]) => folder.parentId === listFolderId);
        if (!relaxProfileTabFolderFilters) {
            if (profileLibraryTab === 'community') {
                entries = entries.filter(([id]) => folderHasDescendantCommunityLibraryItem(id, foldersMap, filesMap));
            } else if (profileLibraryTab === 'media') {
                entries = entries.filter(([id]) => folderHasDescendantVisibleInMyMedia(id, foldersMap, filesMap));
            } else if (profileLibraryTab === 'roomEq') {
                entries = entries.filter(([id]) => folderHasDescendantRoomEqTab(id, foldersMap, filesMap));
            } else if (profileLibraryTab === 'tours') {
                entries = entries.filter(([id]) => folderHasTourInSubtree(id, foldersMap));
            } else if (profileLibraryTab === 'savedVenues') {
                entries = entries.filter(([id, folder]) => {
                    if (folderIsTourRelated(folder)) return false;
                    if (folderHasTourInSubtree(id, foldersMap)) return false;
                    return folderHasSavedVenueForSavedVenuesTab(id, foldersMap, filesMap);
                });
            } else if (profileLibraryTab === 'reports') {
                entries = entries.filter(([id]) => folderHasDescendantReport(id, foldersMap, filesMap));
            }
        }
        return entries.sort(([, a], [, b]) => a.order - b.order);
    };

    // Get files in current directory
    const getCurrentFiles = () => {
        if (savedVenuesFlatView) {
            // Every saved venue in the library (any folder, excluding tours), one row per venue.
            // Only check the venue's ancestors for tour folders: the My Saved Venues root itself holds
            // tour mirror folders, so the subtree check in savedVenueFileBelongsInSavedVenuesTab would hide new saves.
            const foldersMap = userData?.folders || {};
            const seenVenueIds = new Set();
            return Object.entries(userData?.files || {})
                .filter(([_, file]) => {
                    if (file.itemType !== 'savedVenue' || file.tourTabMirrorOf || fileIsUnderTourTree(file, foldersMap)) return false;
                    const venueId = file.data?.id;
                    if (venueId && seenVenueIds.has(venueId)) return false;
                    if (venueId) seenVenueIds.add(venueId);
                    return true;
                })
                .sort(([, a], [, b]) => a.order - b.order);
        }
        let entries = Object.entries(userData?.files || {}).filter(([_, file]) => file.parentId === listFolderId);
        if (profileLibraryTab === 'community') {
            entries = entries.filter(([_, file]) => file.itemType === 'communityPost' || file.itemType === 'communityProfile');
        } else if (profileLibraryTab === 'media') {
            entries = entries.filter(
                ([_, file]) =>
                    file.itemType !== 'communityPost' &&
                    file.itemType !== 'communityProfile' &&
                    !isRoomNotesLibraryItem(file) &&
                    file.itemType !== 'report'
            );
        } else if (profileLibraryTab === 'roomEq') {
            entries = entries.filter(([_, file]) => isRoomNotesLibraryItem(file));
        } else if (profileLibraryTab === 'tours') {
            const foldersMap = userData?.folders || {};
            entries = entries.filter(([_, file]) => {
                if (file.itemType === 'communityPost' || file.itemType === 'communityProfile' || isRoomNotesLibraryItem(file)) return false;
                if (file.itemType === 'savedVenue') {
                    return (
                        fileIsUnderTourTree(file, foldersMap) ||
                        (file.parentId != null && folderHasTourInSubtree(file.parentId, foldersMap))
                    );
                }
                return fileIsUnderTourTree(file, foldersMap);
            });
        } else if (profileLibraryTab === 'savedVenues') {
            const foldersMap = userData?.folders || {};
            entries = entries.filter(([_, file]) => savedVenueFileBelongsInSavedVenuesTab(file, foldersMap));
        } else if (profileLibraryTab === 'reports') {
            entries = entries.filter(([_, file]) => file.itemType === 'report');
        }
        return entries.sort(([, a], [, b]) => a.order - b.order);
    };

    const createFolder = async () => {
        if (!newFolder.name.trim()) {
            setShowErrorModal('Please enter a folder name');
            return;
        }

        const userRef = doc(db, 'users', userData.uid);
        const userDoc = await getDoc(userRef);
        const currentUserData = userDoc.data();

        const folderId = `folder_${Date.now()}`;
        const folderData = {
            name: newFolder.name.trim(),
            color: newFolder.color,
            parentId: listFolderId,
            order: Object.values(currentUserData ? currentUserData.folders : {}).filter(f => f.parentId === listFolderId).length,
            backgroundImg: newFolder.backgroundImg || '',
            createdAt: Timestamp.now(),
            updatedAt: Timestamp.now()
        };

        const updatedFolders = { ...folders, [folderId]: folderData };

        try {
            const userRef = doc(db, 'users', userData.uid);
            await persistUserLibrary(userRef, { folders: updatedFolders });

            setFolders(updatedFolders);
            setUser({ ...userData, folders: updatedFolders });
            setNewFolder({ name: '', color: '#0bb5df', backgroundImg: null });
            setShowCreateFolder(false);

            if (openWithCreateFolder && venueToSave) {
                const newPath = folderPath.length > 0
                    ? [...folderPath, { id: folderId, name: folderData.name }]
                    : [{ id: folderId, name: folderData.name }];
                navigationStateRef.current = { currentFolderId: folderId, folderPath: newPath };
                setCurrentFolderId(folderId);
                setFolderPath(newPath);
                if (profileLibraryTab === 'tours') {
                    try {
                        setLoading(true);
                        const { folders: nextFolders, files: nextFiles } = await applyVenueToTourFolderInUserLibrary(
                            userRef,
                            folderId,
                            venueToSave
                        );
                        setFolders(nextFolders);
                        setFiles(nextFiles);
                        setUser({ ...userData, folders: nextFolders, files: nextFiles });
                    } catch (e) {
                        console.error('createFolder (tour)', e);
                        setShowErrorModal(e?.message || 'Failed to create tour');
                    } finally {
                        setLoading(false);
                    }
                } else {
                    await addVenueToMediaManager(venueToSave);
                }
            }
        } catch (error) {
            console.error('Error creating folder:', error);
            setShowErrorModal('Failed to create folder');
        }
    };

    const deleteFolder = async (folderId) => {
        const folder = folders[folderId];
        
        // Check if folder is protected
        if (folder.protected) {
            setShowErrorModal('This folder is protected and cannot be deleted');
            return;
        }
        
        if (deleteConfirmText !== folder.name) {
            setShowErrorModal('Folder name does not match');
            return;
        }

        try {
            // Get all folders and files that need to be deleted (recursively)
            const { foldersToDelete, filesToDelete } = getAllNestedItems(folderId);
            
            // Delete all files from storage (with smart deletion logic)
            let storageDeleteCount = 0;
            let storageSkipCount = 0;
            
            for (const [fileId, file] of filesToDelete) {
                try {
                    // Only try to delete from storage if it's a file we actually uploaded to our storage
                    if (file.storagePath && 
                        file.storagePath.startsWith('userMedia/') && 
                        !file.migratedFrom && 
                        !file.uploadSource) {
                        
                        const storageRef = ref(storage, file.storagePath);
                        await deleteObject(storageRef);
                        storageDeleteCount++;
                    } else {
                        storageSkipCount++;
                    }
                } catch (error) {
                    console.warn(`⚠️ Could not delete file ${file.name} from storage:`, error);
                    storageSkipCount++;
                }
            }

            // Remove all folders and files from database
            const updatedFolders = { ...folders };
            const updatedFiles = { ...files };

            // Delete all nested folders
            foldersToDelete.forEach(([folderId]) => {
                delete updatedFolders[folderId];
            });

            // Delete all files
            filesToDelete.forEach(([fileId]) => {
                delete updatedFiles[fileId];
            });

            // Update database
            const userRef = doc(db, 'users', userData.uid);
            await persistUserLibrary(userRef, {
                folders: updatedFolders,
                files: updatedFiles
            });

            // Update local state
            setFolders(updatedFolders);
            setFiles(updatedFiles);
            setUser({ ...userData, folders: updatedFolders, files: updatedFiles });
            setShowDeleteConfirm(null);
            setDeleteConfirmText('');

            // Show success message
            const summary = 
                `Successfully deleted folder "${folder.name}" and all its contents:\n\n` +
                `• ${foldersToDelete.length} folders deleted\n` +
                `• ${filesToDelete.length} files removed\n` +
                `• ${storageDeleteCount} files deleted from storage\n` +
                `• ${storageSkipCount} files skipped (external/migrated)`;
            
            setShowSuccessModal(summary);

        } catch (error) {
            console.error('❌ Error deleting folder:', error);
            setShowErrorModal(`Failed to delete folder: ${error.message}`);
        }
    };

    // Helper function to recursively get all nested folders and files
    const getAllNestedItems = (parentFolderId) => {
        const foldersToDelete = [];
        const filesToDelete = [];
        
        // Function to recursively collect items
        const collectItems = (folderId) => {
            // Add current folder to deletion list
            if (folders[folderId]) {
                foldersToDelete.push([folderId, folders[folderId]]);
            }
            
            // Get all files in current folder
            const folderFiles = Object.entries(files).filter(([_, file]) => file.parentId === folderId);
            filesToDelete.push(...folderFiles);
            
            // Get all subfolders and recursively collect their items
            const subfolders = Object.entries(folders).filter(([_, folder]) => folder.parentId === folderId);
            subfolders.forEach(([subfolderId]) => {
                collectItems(subfolderId);
            });
        };
        
        // Start collection from the target folder
        collectItems(parentFolderId);
        
        return { foldersToDelete, filesToDelete };
    };

    const uploadFileToFolder = async (folderId) => {
        if (!allowFileUpload) {
            Alert.alert('Not available', 'File uploads are disabled in this view.');
            return;
        }
        try {
            setLoading(true);
            const result = await DocumentPicker.getDocumentAsync({
                type: [
                    'image/*',
                    'video/mp4',
                    'audio/mp3',
                    'application/pdf',
                    'application/msword',
                    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                    'text/plain',
                    'audio/*',
                    'video/*'
                ],
                multiple: true
            });

            if (!result.canceled && result.assets && result.assets.length > 0) {
                const uploadPromises = result.assets.map(async (file) => {
                    const fileUri = file.uri;
                    const response = await fetch(fileUri);
                    const fileBlob = await response.blob();

                    const fileExt = file.name.split('.').pop();
                    const fileName = `${Date.now()}_${Math.random().toString(36).substr(2, 9)}.${fileExt}`;
                    const storagePath = `userMedia/${userData.uid}/${folderId}/${fileName}`;
                    const storageRef = ref(storage, storagePath);

                    await uploadBytes(storageRef, fileBlob);
                    const downloadURL = await getDownloadURL(storageRef);

                    const fileId = `item_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
                    const fileExtLower = fileExt.toLowerCase();
                    let fileType = 'image';
                    
                    // Determine file type based on extension
                    if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'].includes(fileExtLower)) {
                        fileType = 'image';
                    } else if (['mp4', 'avi', 'mov', 'wmv', 'flv', 'webm', 'mkv'].includes(fileExtLower)) {
                        fileType = 'video';
                    } else if (['mp3', 'wav', 'aac', 'flac', 'ogg', 'm4a', 'wma'].includes(fileExtLower)) {
                        fileType = 'audio';
                    } else if (['pdf', 'doc', 'docx', 'txt', 'rtf'].includes(fileExtLower)) {
                        fileType = 'document';
                    }
                    
                    return {
                        [fileId]: {
                            name: file.name,
                            id: fileId,
                            parentId: folderId,
                            itemType: 'file',
                            data: {
                                fileType: fileType,
                                url: downloadURL,
                                manualUpload: true
                            },
                            order: Object.values(files).filter(f => f.parentId === folderId).length,
                            createdAt: Timestamp.now(),
                            updatedAt: Timestamp.now()
                        }
                    };
                });

                const uploadedFiles = await Promise.all(uploadPromises);
                const newFiles = uploadedFiles.reduce((acc, file) => ({ ...acc, ...file }), {});
                const updatedFiles = { ...files, ...newFiles };

                const userRef = doc(db, 'users', userData.uid);
                await persistUserLibrary(userRef, { files: updatedFiles });

                setFiles(updatedFiles);

                setUser({ ...userData, files: updatedFiles });

                Alert.alert('Success', `${uploadedFiles.length} file(s) uploaded successfully!`);
            }
        } catch (error) {
            console.error('Error uploading files:', error);
            Alert.alert('Error', 'Failed to upload files');
        } finally {
            setLoading(false);
        }
    };

    const uploadFolderBackground = async (folderId) => {
        if (!allowFileUpload) {
            return;
        }
        try {
            const result = await DocumentPicker.getDocumentAsync({ type: 'image/*' });

            if (!result.canceled && result.assets && result.assets.length > 0) {
                const file = result.assets[0];
                const fileUri = file.uri;
                const response = await fetch(fileUri);
                const fileBlob = await response.blob();

                const fileExt = file.name.split('.').pop();
                const fileName = `folder_bg_${folderId}_${Date.now()}.${fileExt}`;
                const storagePath = `userMedia/${userData.uid}/folder_backgrounds/${fileName}`;
                const storageRef = ref(storage, storagePath);

                await uploadBytes(storageRef, fileBlob);
                const downloadURL = await getDownloadURL(storageRef);

                const updatedFolders = {
                    ...folders,
                    [folderId]: {
                        ...folders[folderId],
                        backgroundImg: downloadURL,
                        updatedAt: Timestamp.now()
                    }
                };

                const userRef = doc(db, 'users', userData.uid);
                await persistUserLibrary(userRef, { folders: updatedFolders });

                setFolders(updatedFolders);
                setUser({ ...userData, folders: updatedFolders });
                // setUser({ ...userData, folders: updatedFolders });
            }
        } catch (error) {
            console.error('Error uploading folder background:', error);
            Alert.alert('Error', 'Failed to upload folder background');
        }
    };

    const deleteFile = async (fileId) => {
        try {
            const folderProtected = folders[files[fileId].parentId].protected;
            
            const file = files[fileId];
            if (!file) {
                setShowErrorModal('File not found');
                return;
            }
            
            // Only try to delete from storage if it's a file we actually uploaded to our storage
            // Skip storage deletion for migrated files or files from external sources
            if (file.storagePath && 
                file.storagePath.startsWith('userMedia/') && 
                !folderProtected) {
                
                try {
                    const storageRef = ref(storage, file.storagePath);
                    await deleteObject(storageRef);
                } catch (storageError) {
                    console.warn('⚠️ Could not delete file from storage (file may not exist):', storageError);
                    // Continue with database deletion even if storage deletion fails
                }
            }

            // Always remove from database
            const updatedFiles = { ...files };
            delete updatedFiles[fileId];

            const userRef = doc(db, 'users', userData.uid);
            await persistUserLibrary(userRef, { files: updatedFiles });
            setFiles(updatedFiles);
            setUser({ ...userData, files: updatedFiles });

            setShowSuccessModal(`File "${file.name}" has been deleted from your folder system.`);

        } catch (error) {
            console.error('❌ Error deleting file:', error);
            setShowErrorModal(`Failed to delete file: ${error.message}\n\nCheck the console for more details.`);
        }
    };

    // Rename functionality
    const renameItem = async (itemId, itemType, newName) => {
        if (!newName.trim()) {
            setShowErrorModal('Please enter a valid name');
            return;
        }

        try {
            if (itemType === 'folder') {
                const updatedFolders = {
                    ...folders,
                    [itemId]: {
                        ...folders[itemId],
                        name: newName.trim(),
                        updatedAt: Timestamp.now()
                    }
                };

                const userRef = doc(db, 'users', userData.uid);
                await persistUserLibrary(userRef, { folders: updatedFolders });
                setFolders(updatedFolders);
                setUser({ ...userData, folders: updatedFolders });
            } else {
                const prev = files[itemId];
                const base = {
                    ...prev,
                    name: newName.trim(),
                    updatedAt: Timestamp.now(),
                };
                const nextFile =
                    prev?.itemType === 'communityPost' && prev?.data
                        ? { ...base, data: { ...prev.data, postTitle: newName.trim() } }
                        : prev?.itemType === 'communityProfile' && prev?.data
                            ? { ...base, data: { ...prev.data, savedName: newName.trim() } }
                        : base;
                const updatedFiles = {
                    ...files,
                    [itemId]: nextFile,
                };

                const userRef = doc(db, 'users', userData.uid);
                await persistUserLibrary(userRef, { files: updatedFiles });
                setFiles(updatedFiles);
                setUser({ ...userData, files: updatedFiles });
            }

            setShowRenameModal(null);
            setRenameText('');
        } catch (error) {
            console.error('Error renaming item:', error);
            setShowErrorModal('Failed to rename item');
        }
    };

    // Change folder color functionality
    const changeFolderColor = async (folderId, newColor) => {
        try {
            const updatedFolders = {
                ...folders,
                [folderId]: {
                    ...folders[folderId],
                    color: newColor,
                    updatedAt: Timestamp.now()
                }
            };

            const userRef = doc(db, 'users', userData.uid);
            await persistUserLibrary(userRef, { folders: updatedFolders });
            setFolders(updatedFolders);
            setUser({ ...userData, folders: updatedFolders });
            setShowColorModal(null);
        } catch (error) {
            console.error('Error changing folder color:', error);
            setShowErrorModal('Failed to change folder color');
        }
    };

    const renderFolder = (folderId, folder, index) => {
        // Get subfolders and files count
        const subfolders = Object.values(folders).filter(f => f.parentId === folderId);
        const folderFiles = Object.entries(files).filter(([_, file]) => file.parentId === folderId);
        const totalItems = subfolders.length + folderFiles.length;

        const isSelected = selectedItem && selectedItem.id === folderId;
        
        // Check if folder has matching files for search indicator
        const hasMatchingFiles = searchQuery.trim() && folderFiles.some(([_, file]) => 
            file.name.toLowerCase().includes(searchQuery.toLowerCase())
        );

        return (
            <View
                key={folderId}
                style={mediaManagerStyles.folderContainer}>
                <div
                    style={mediaManagerStyles.folderWrapper}
                    onMouseEnter={() => {
                        setHoverDropFolderId(folderId);
                        hoverDropFolderRef.current = folderId;
                    }}
                    onMouseLeave={() => {
                        setHoverDropFolderId(null);
                        hoverDropFolderRef.current = null;
                    }}
                    {...(hasMouseSupport() && Platform.OS === 'web' && {
                        onContextMenu: (e) => {
                            e.preventDefault();
                            const { clientX, clientY } = e.nativeEvent;
                            // Only show context menu for non-protected folders
                            if (!folder.protected) {
                                setContextMenu({
                                    item: { id: folderId, type: 'folder', item: folder },
                                    position: { x: clientX, y: clientY }
                                });
                            }
                        }
                    })}
                >
                    <TouchableOpacity
                        onPress={() => handleItemPress(folderId, 'folder', folder, () => {
                            navigateToFolder(folderId);
                            setSelectedItem(null);
                        })}
                        onLongPress={(e) => {
                            // Long press for touch devices (mobile/tablet)
                            if (hasTouchSupport()) {
                                const { pageX, pageY } = e.nativeEvent;
                            // Only show context menu for non-protected folders
                                if (!folder.protected) {
                                    setContextMenu({
                                        item: { id: folderId, type: 'folder', item: folder },
                                        position: { x: pageX, y: pageY }
                                    });
                                }
                            }
                        }}
                        style={[
                            mediaManagerStyles.folderTouchable,
                            isSelected && mediaManagerStyles.folderTouchableSelected,
                            isSelected && { borderColor: tx, shadowColor: tx },
                        ]}>
                        {/* Folder Tab (protruding part) */}
                        <View style={[
                            mediaManagerStyles.folderTab,
                            { backgroundColor: folder.color }
                        ]} />
                        
                        {/* Search match indicator */}
                        {hasMatchingFiles && (
                            <View style={mediaManagerStyles.searchMatchIndicator}>
                                <Ionicons name="search" size={10} color={tx} />
                            </View>
                        )}

                        {/* Main folder body */}
                        <View style={[
                            mediaManagerStyles.folderBody,
                            // { backgroundColor: folder.color }
                        ]}>
                            {/* Background image if exists */}
                            {folder.backgroundImg && (
                                <Image
                                    source={{ uri: folder.backgroundImg }}
                                    style={mediaManagerStyles.folderBackgroundImage}
                                    resizeMode="cover"
                                />
                            )}

                            {/* Folder gradient overlay */}
                            <LinearGradient
                                colors={[folder.color, folder.color + 'CC']}
                                style={mediaManagerStyles.folderGradient}
                            >
                                {folder.backgroundImg && (
                                    <Image 
                                        source={{ uri: folder.backgroundImg }} 
                                        style={mediaManagerStyles.folderGradientImage} 
                                        resizeMode="cover" 
                                    />
                                )}
                            </LinearGradient>
                        </View>


                    </TouchableOpacity>

                    {/* Folder name and info */}
                    <View style={mediaManagerStyles.folderInfo}>
                        <View style={mediaManagerStyles.folderInfoRow}>
                            <Text style={[mediaManagerStyles.folderName, { color: tx }]} numberOfLines={1}>{folder.name}</Text>
                            {folder.protected && (
                                <Ionicons name="shield-checkmark" size={12} color="#2ecc71" />
                            )}
                        </View>
                        <Text style={[mediaManagerStyles.folderDetails, { color: tx }]}>
                            {totalItems} items{folder.protected ? ' • Protected' : ''}
                            {hasMatchingFiles ? ' • Contains matches' : ''}
                        </Text>
                    </View>
                </div>
            </View>
        );
    };

    const renderItem = (itemId, item, index) => {
        const isSelected = selectedItem && selectedItem.id === itemId;
        const itemType = item.itemType;

        const handleItemPressWrapper = () => {
            handleItemPress(itemId, 'item', item, () => {
                // Double tap: Open in MediaViewer
                const mediaArray = getCurrentFolderMedia();
                const currentIndex = mediaArray.findIndex(media => media.id === itemId);
                if (currentIndex !== -1) {
                    setMediaViewerMedia(mediaArray);
                    setSelectedMediaIndex(currentIndex);
                    setIsMediaViewerVisible(true);
                }
                setSelectedItem(null);
            });
        };

        const handleLongPress = (e) => {
            if (hasTouchSupport()) {
                const { pageX, pageY } = e.nativeEvent;
                setContextMenu({
                    item: { id: itemId, type: 'file', item: item },
                    position: { x: pageX, y: pageY }
                });
            }
        };

        // Handle different item types
        if (itemType === 'savedVenue') {
            const venueLabel = item.data?.name || item.name || 'Venue';
            return (
                <View key={itemId} style={mediaManagerStyles.folderContainer}>
                    <ItemWrapper
                        isSelected={isSelected}
                        onPress={handleItemPressWrapper}
                        onLongPress={handleLongPress}
                        itemType="savedVenue"
                    >
                        <VenueCardFile
                            venue={{
                                ...item.data,
                                imageUrl: item.data?.imageUrl || item.data?.url
                            }}
                            isSelected={isSelected}
                            onPress={() => {}} 
                            onLongPress={() => {}} 
                            onView={onViewVenue}
                            onOpenNotes={handleOpenNotes}
                            onOpenEq={handleOpenEq}
                            hasRoomEq={!!userData?.venueIdsWithRoomEq?.[item.data?.id]}
                            onDeletePhoto={onDeleteVenuePhoto}
                            onAddPhoto={onAddVenuePhoto}
                            userData={userData}
                        />
                    </ItemWrapper>
                    <View style={mediaManagerStyles.folderInfo}>
                        <View style={mediaManagerStyles.folderInfoRow}>
                            <Text style={[mediaManagerStyles.folderName, { color: tx }]} numberOfLines={2}>
                                {venueLabel}
                            </Text>
                        </View>
                    </View>
                </View>
            );
        } 
        else if (itemType === 'report') {
            const reportLabel = item.name || item.data?.venueName || 'Report';
            return (
                <View key={itemId} style={mediaManagerStyles.folderContainer}>
                    <ItemWrapper
                        isSelected={isSelected}
                        onPress={handleItemPressWrapper}
                        onLongPress={handleLongPress}
                        itemType="report"
                    >
                        <ReportCardFile
                            report={item.data}
                            isSelected={isSelected}
                            onPress={() => {}} 
                            onLongPress={() => {}} 
                            onEdit={onEditReport}
                            onDelete={onDeleteReport}
                        />
                    </ItemWrapper>
                    <View style={mediaManagerStyles.folderInfo}>
                        <View style={mediaManagerStyles.folderInfoRow}>
                            <Text style={[mediaManagerStyles.folderName, { color: tx }]} numberOfLines={2}>
                                {reportLabel}
                            </Text>
                        </View>
                    </View>
                </View>
            );
        } else if (itemType === ITEM_TYPE_ROOM_EQ_SUBMISSION) {
            const venueId = item.data?.venueId;
            const submissionId = item.data?.submissionId;
            const displayName =
                item.name ||
                (item.data?.venueName ? `${String(item.data.venueName).trim()} room info` : 'Room info');
            const previewImageUrl = item.data?.previewImageUrl || null;
            const fallbackVenueImageUrl = venueId ? userData?.savedVenueImages?.[venueId] : null;

            const viewSubmissionInModal = () => {
                setSelectedItem(null);
                openRoomEqSubmissionViewer(venueId, submissionId);
            };

            const handleRoomEqSubmissionPress = () => {
                handleItemPress(itemId, 'item', item, viewSubmissionInModal);
            };

            const viewVenueFromCard = () => {
                if (venueId && onViewVenue) {
                    onViewVenue({
                        id: venueId,
                        name: item.data?.venueName || displayName,
                    });
                }
            };

            return (
                <View key={itemId} style={mediaManagerStyles.folderContainer}>
                    <ItemWrapper
                        isSelected={isSelected}
                        onPress={handleRoomEqSubmissionPress}
                        onLongPress={handleLongPress}
                        itemType={ITEM_TYPE_ROOM_EQ_SUBMISSION}
                    >
                        <RoomEqSubmissionCardFile
                            displayName={displayName}
                            previewImageUrl={previewImageUrl}
                            fallbackVenueImageUrl={fallbackVenueImageUrl}
                            onViewVenue={viewVenueFromCard}
                            onViewSubmission={viewSubmissionInModal}
                        />
                    </ItemWrapper>
                    <View style={mediaManagerStyles.folderInfo}>
                        <View style={mediaManagerStyles.folderInfoRow}>
                            <Text style={[mediaManagerStyles.folderName, { color: tx }]} numberOfLines={2}>
                                {displayName}
                            </Text>
                        </View>
                    </View>
                </View>
            );
        } else if (itemType === 'roomNotes' || itemType === 'roomEqReference') {
            const openRoomNotes = () => {
                if (item.data?.venueId && item.data?.submissionId && typeof onEditRoomEqSubmission === 'function') {
                    onEditRoomEqSubmission({
                        venueId: item.data.venueId,
                        id: item.data.submissionId,
                    });
                } else if (item.data?.venueId && onViewVenue) {
                    onViewVenue({
                        id: item.data.venueId,
                        name: item.name,
                    });
                }
                setSelectedItem(null);
            };
            const roomShortcutLabel =
                item.name || (itemType === 'roomNotes' ? 'Room notes' : 'Room EQ ref');
            return (
                <View key={itemId} style={mediaManagerStyles.folderContainer}>
                    <ItemWrapper
                        isSelected={isSelected}
                        onPress={() => handleItemPress(itemId, 'item', item, openRoomNotes)}
                        onLongPress={handleLongPress}
                        itemType={itemType}
                    >
                        <View
                            style={[
                                mediaManagerStyles.filePlaceholder,
                                {
                                    backgroundColor: itemType === 'roomNotes' ? '#5b21b6' : '#208c6a',
                                    borderWidth: StyleSheet.hairlineWidth,
                                    borderColor: theme.ui.borderStrong,
                                },
                            ]}
                        >
                            <Ionicons
                                name={itemType === 'roomNotes' ? 'reader-outline' : 'pulse'}
                                size={roomNoteLibraryIconSize}
                                color={tx}
                            />
                            <Text style={[mediaManagerStyles.filePlaceholderText, { color: tx, fontSize: filePlaceholderFontSize }]} numberOfLines={2}>
                                {item.name || (itemType === 'roomNotes' ? 'Room notes' : 'Room EQ')}
                            </Text>
                        </View>
                    </ItemWrapper>
                    <View style={mediaManagerStyles.folderInfo}>
                        <View style={mediaManagerStyles.folderInfoRow}>
                            <Text style={[mediaManagerStyles.folderName, { color: tx }]} numberOfLines={2}>
                                {roomShortcutLabel}
                            </Text>
                        </View>
                    </View>
                </View>
            );
        } else if (itemType === 'communityProfile') {
            const handleProfileDoublePress = () => {
                setCommunityProfileModalData(item.data);
                setShowCommunityProfileModal(true);
                setSelectedItem(null);
            };
            const displayName =
                item.name ||
                item.data?.savedName ||
                item.data?.username ||
                item.data?.title ||
                'Community profile';
            const subtitle = item.data?.title || item.data?.location || item.data?.entityType || '';
            return (
                <View key={itemId} style={mediaManagerStyles.folderContainer}>
                    <ItemWrapper
                        isSelected={isSelected}
                        onPress={() => handleItemPress(itemId, 'item', item, handleProfileDoublePress)}
                        onLongPress={handleLongPress}
                        itemType="communityProfile"
                    >
                        <View style={[mediaManagerStyles.filePlaceholder, { backgroundColor: '#feffe4', borderWidth: StyleSheet.hairlineWidth, borderColor: theme.ui.border }]}>
                            {item.data?.photoURL ? (
                                <Image source={{ uri: item.data.photoURL }} style={{ width: 64, height: 64, borderRadius: 32, marginBottom: 8 }} />
                            ) : (
                                <View style={{ width: 64, height: 64, borderRadius: 32, marginBottom: 8, backgroundColor: '#d9c8a5', alignItems: 'center', justifyContent: 'center' }}>
                                    <Text style={{ fontFamily: 'VastagoGrotesk', fontSize: 24, color: '#4b3b1f' }}>
                                        {(displayName || 'P').charAt(0).toUpperCase()}
                                    </Text>
                                </View>
                            )}
                            <Text style={[mediaManagerStyles.filePlaceholderText, { color: '#4b3b1f', fontSize: filePlaceholderFontSize }]} numberOfLines={2} ellipsizeMode="tail">
                                {displayName}
                            </Text>
                            {subtitle ? (
                                <Text style={[mediaManagerStyles.filePlaceholderText, { color: '#7a6a52', fontSize: Math.max(10, filePlaceholderFontSize - 1) }]} numberOfLines={1}>
                                    {subtitle}
                                </Text>
                            ) : null}
                        </View>
                    </ItemWrapper>
                    <View style={mediaManagerStyles.folderInfo}>
                        <View style={mediaManagerStyles.folderInfoRow}>
                            <Text
                                style={[mediaManagerStyles.folderName, { color: tx }]}
                                numberOfLines={1}
                                ellipsizeMode="middle"
                                onPress={() => {
                                    setRenameText(displayName);
                                    setShowRenameModal({ id: itemId, type: 'file', item });
                                }}
                            >
                                {displayName}
                            </Text>
                        </View>
                    </View>
                </View>
            );
        } else if (itemType === 'communityPost') {
            const handlePostDoublePress = () => {
                setCommunityPostModalData(item.data);
                setShowCommunityPostModal(true);
                setSelectedItem(null);
            };
            const displayName = item.name || item.data?.postTitle || 'Community post';
            return (
                <View key={itemId} style={mediaManagerStyles.folderContainer}>
                    <ItemWrapper
                        isSelected={isSelected}
                        onPress={() => handleItemPress(itemId, 'item', item, handlePostDoublePress)}
                        onLongPress={handleLongPress}
                        itemType="communityPost"
                    >
                        <CommunityPostCardFile post={item.data} previewOnly isSelected={isSelected} />
                    </ItemWrapper>
                    <View style={mediaManagerStyles.folderInfo}>
                        <View style={mediaManagerStyles.folderInfoRow}>
                            <Text
                                style={[mediaManagerStyles.folderName, { color: tx }]}
                                numberOfLines={1}
                                ellipsizeMode="middle"
                                onPress={() => {
                                    setRenameText(item.name || item.data?.postTitle || '');
                                    setShowRenameModal({ id: itemId, type: 'file', item });
                                }}
                            >
                                {displayName}
                            </Text>
                        </View>
                    </View>
                </View>
            );
        }
        else {
            // Handle file type
            return (
                <View key={itemId} style={mediaManagerStyles.folderContainer}>
                    <ItemWrapper
                        isSelected={isSelected}
                        onPress={handleItemPressWrapper}
                        onLongPress={handleLongPress}
                        itemType="file"
                    >
                        {renderFileContent(itemId, item, index)}
                    </ItemWrapper>
                    <View style={mediaManagerStyles.folderInfo}>
                        <View style={mediaManagerStyles.folderInfoRow}>
                            <Text
                                style={[mediaManagerStyles.folderName, { color: tx }]}
                                numberOfLines={1}
                                ellipsizeMode="middle"
                                onPress={() => {
                                    setRenameText(item.name || '');
                                    setShowRenameModal({ id: itemId, type: 'file', item });
                                }}
                            >
                                {item.name || 'Untitled'}
                            </Text>
                        </View>
                    </View>
                </View>
            );
        }
    };

    const renderFileContent = (fileId, file, index) => {
        const fileType = getFileType(file);
        const fileUrl = file.data?.url || file.url;
        
        // If it's an image, show the actual image
        if (fileType === 'image' || fileType === 'photos') {
            return (
                <Image
                    source={{ uri: fileUrl }}
                    style={mediaManagerStyles.fileImage}
                    onError={() => {
                        // If image fails to load, it might be a non-image file
                    }}
                />
            );
        }
        
        // Otherwise, show appropriate placeholder based on file type
        if (fileType === 'pdf' || fileType === 'documents') {
            return (
                <View style={[mediaManagerStyles.filePlaceholder, mediaManagerStyles.pdfBackground]}>
                    <Ionicons name="document-text" size={fileLibraryIconSize} color={tx} />
                </View>
            );
        }
        
        if (fileType === 'document') {
            return (
                <View style={[mediaManagerStyles.filePlaceholder, mediaManagerStyles.documentBackground]}>
                    <Ionicons name="document" size={fileLibraryIconSize} color={tx} />
                    <Text style={[mediaManagerStyles.filePlaceholderText, { color: tx, fontSize: filePlaceholderFontSize }]} numberOfLines={2} ellipsizeMode="middle">
                        {file.name}
                    </Text>
                </View>
            );
        }
        
        if (fileType === 'audio') {
            return (
                <View style={[mediaManagerStyles.filePlaceholder, mediaManagerStyles.audioBackground]}>
                    <Ionicons name="musical-notes" size={fileLibraryIconSize} color={tx} />
                    <Text style={[mediaManagerStyles.filePlaceholderText, { color: tx, fontSize: filePlaceholderFontSize }]} numberOfLines={2} ellipsizeMode="middle">
                        {file.name}
                    </Text>
                </View>
            );
        }
        
        if (fileType === 'video') {
            return (
                <View style={[mediaManagerStyles.filePlaceholder, mediaManagerStyles.videoBackground]}>
                    <Ionicons name="play-circle" size={fileLibraryIconSize} color={tx} />
                    <Text style={[mediaManagerStyles.filePlaceholderText, { color: tx, fontSize: filePlaceholderFontSize }]} numberOfLines={2} ellipsizeMode="middle">
                        {file.name}
                    </Text>
                </View>
            );
        }
        
        // Default fallback
        return (
            <View style={[mediaManagerStyles.filePlaceholder, mediaManagerStyles.defaultBackground]}>
                <Ionicons name="document" size={fileLibraryIconSize} color={tx} />
                <Text style={[mediaManagerStyles.filePlaceholderText, { color: tx, fontSize: filePlaceholderFontSize }]} numberOfLines={2} ellipsizeMode="middle">
                    {file.name}
                </Text>
            </View>
        );
    };

    const renderFile = (fileId, file, index) => {
        const isSelected = selectedItem && selectedItem.id === fileId;

        return (
            <View
                key={fileId}
                style={mediaManagerStyles.legacyFileContainer}>
                <div
                    {...(hasMouseSupport() && {
                        onContextMenu: (e) => {
                            // Right-click for desktop with mouse
                            e.preventDefault();
                            const { clientX, clientY } = e.nativeEvent;
                            // Allow context menu for all files (protected status removed for files)
                            setContextMenu({
                                    item: { id: fileId, type: 'file', item: file },
                                    position: { x: clientX, y: clientY }
                                });
                        }
                    })}
                    style={mediaManagerStyles.legacyFileWrapper}
                >
                    <TouchableOpacity
                        key={fileId}
                        onPress={() => handleItemPress(fileId, 'file', file, () => {
                            // Double tap: Handle different file types
                            if (file.url) {
                                const fileType = getFileType(file);
                                
                                // For all files with URLs, open in MediaViewer
                                const folderImages = getCurrentFolderImages();
                                const imageUrls = Object.values(folderImages);
                                const currentIndex = imageUrls.findIndex(url => url === file.url);
                                
                                if (currentIndex !== -1) {
                                    // For images found in folder images, use existing logic
                                    handleMediaPress(currentIndex);
                                } else {
                                    // For any file not found in folder images, create a single-item media array
                                    const singleMediaArray = [{
                                        id: fileId,
                                        name: file.name,
                                        url: file.url,
                                        type: fileType
                                    }];
                                    
                                    // Set the media and open MediaViewer
                                    setMediaViewerMedia(singleMediaArray);
                                    setSelectedMediaIndex(0);
                                    setIsMediaViewerVisible(true);
                                }
                            } else {
                                setShowSuccessModal(`Opening ${file.name}`);
                            }
                            setSelectedItem(null);
                        })}
                        onLongPress={(e) => {
                            // Long press for touch devices (mobile/tablet)
                            if (hasTouchSupport()) {
                                const { pageX, pageY } = e.nativeEvent;
                            // Allow context menu for all files (protected status removed for files)
                                    setContextMenu({
                                        item: { id: fileId, type: 'file', item: file },
                                        position: { x: pageX, y: pageY }
                                    });
                            }
                        }}
                        style={[
                            mediaManagerStyles.legacyFileTouchable,
                            isSelected && mediaManagerStyles.legacyFileTouchableSelected,
                            isSelected && { borderColor: tx, shadowColor: tx },
                        ]}>
                        {(() => {
                            const fileType = getFileType(file);
                            
                            // If it's an image, show the actual image
                            if (fileType === 'image' || fileType === 'photos') {
                                return (
                        <Image
                            source={{ uri: file.url }}
                            style={mediaManagerStyles.fileImage}
                                        onError={() => {
                                            // If image fails to load, it might be a non-image file
                                        }}
                                    />
                                );
                            }
                            
                            // Otherwise, show appropriate placeholder based on file type
                            if (fileType === 'pdf' || fileType === 'documents') {
                                return (
                                    <View style={[mediaManagerStyles.filePlaceholder, mediaManagerStyles.pdfBackground]}>
                                        <Ionicons name="document-text" size={fileLibraryIconSize} color={tx} />
                                    </View>
                                );
                            }
                            
                            if (fileType === 'document') {
                                return (
                                    <View style={[mediaManagerStyles.filePlaceholder, mediaManagerStyles.documentBackground]}>
                                        <Ionicons name="document" size={fileLibraryIconSize} color={tx} />
                                        <Text style={[mediaManagerStyles.filePlaceholderText, { color: tx, fontSize: filePlaceholderFontSize }]} numberOfLines={2} ellipsizeMode="middle">
                                            {file.name}
                                        </Text>
                                    </View>
                                );
                            }
                            
                            if (fileType === 'audio') {
                                return (
                                    <View style={[mediaManagerStyles.filePlaceholder, mediaManagerStyles.audioBackground]}>
                                        <Ionicons name="musical-notes" size={fileLibraryIconSize} color={tx} />
                                        <Text style={[mediaManagerStyles.filePlaceholderText, { color: tx, fontSize: filePlaceholderFontSize }]} numberOfLines={2} ellipsizeMode="middle">
                                            {file.name}
                                        </Text>
                                    </View>
                                );
                            }
                            
                            if (fileType === 'video') {
                                return (
                                    <View style={[mediaManagerStyles.filePlaceholder, mediaManagerStyles.videoBackground]}>
                                        <Ionicons name="play-circle" size={fileLibraryIconSize} color={tx} />
                                        <Text style={[mediaManagerStyles.filePlaceholderText, { color: tx, fontSize: filePlaceholderFontSize }]} numberOfLines={2} ellipsizeMode="middle">
                                            {file.name}
                                        </Text>
                                    </View>
                                );
                            }
                            
                            // Default fallback
                            return (
                                <View style={[mediaManagerStyles.filePlaceholder, mediaManagerStyles.defaultBackground]}>
                                    <Ionicons name="attach" size={fileLibraryIconSize} color={tx} />
                                    <Text style={[mediaManagerStyles.filePlaceholderText, { color: tx, fontSize: filePlaceholderFontSize }]} numberOfLines={2} ellipsizeMode="middle">
                                        {file.name}
                                    </Text>
                                </View>
                            );
                        })()}



                        <LinearGradient
                            colors={['rgba(0,0,0,0)', 'rgba(0,0,0,0.5)']}
                            style={mediaManagerStyles.legacyFileGradient}
                        >
                            <Text style={[mediaManagerStyles.legacyFileName, { color: tx }]} numberOfLines={1}>{file.name}</Text>
                        </LinearGradient>
                    </TouchableOpacity>
                </div>
            </View>

        );
    };

    // Context menu component
    const renderContextMenu = () => {
        if (!contextMenu) return null;

        const { item, position } = contextMenu;

        return (
            <Modal
                visible={true}
                transparent={true}
                animationType="none"
                onRequestClose={() => setContextMenu(null)}
            >
                <TouchableOpacity
                    style={{ flex: 1 }}
                    activeOpacity={1}
                    onPress={() => setContextMenu(null)}
                >
                    <View style={[
                        mediaManagerStyles.contextMenu,
                        {
                            left: Math.min(position.x, window.innerWidth - 150), // Ensure menu doesn't go off-screen
                            top: Math.min(position.y, window.innerHeight - 200), // Ensure menu doesn't go off-screen
                        }
                    ]}>
                        {/* Add background image option (folders only) */}
                        {item.type === 'folder' && allowFileUpload && (
                            <TouchableOpacity
                                onPress={() => {
                                    uploadFolderBackground(item.id);
                                    setContextMenu(null);
                                }}
                                style={mediaManagerStyles.contextMenuItem}
                            >
                                <Ionicons name="image" size={16} color="#333" />
                                <Text style={mediaManagerStyles.contextMenuText}>Add Background</Text>
                            </TouchableOpacity>
                        )}

                        {/* Edit color option (folders only) */}
                        {item.type === 'folder' && (
                            <TouchableOpacity
                                onPress={() => {
                                    setSelectedColor(item.item.color || '#0bb5df');
                                    setShowColorModal(item);
                                    setContextMenu(null);
                                }}
                                style={mediaManagerStyles.contextMenuItem}
                            >
                                <Ionicons name="color-palette" size={16} color="#333" />
                                <Text style={mediaManagerStyles.contextMenuText}>Edit Color</Text>
                            </TouchableOpacity>
                        )}

                        {/* Rename option - hide for venues and reports */}
                        {!(item.type === 'item' && (item.item.itemType === 'savedVenue' || item.item.itemType === 'report' || item.item.itemType === 'communityPost' || item.item.itemType === 'communityProfile' || item.item.itemType === 'roomEqReference' || item.item.itemType === 'roomNotes' || item.item.itemType === ITEM_TYPE_ROOM_EQ_SUBMISSION)) && (
                            <TouchableOpacity
                                onPress={() => {
                                    setRenameText(item.item.name || '');
                                    setShowRenameModal(item);
                                    setContextMenu(null);
                                }}
                                style={mediaManagerStyles.contextMenuItem}
                            >
                                <Ionicons name="create" size={16} color={theme.text.primary} />
                                <Text style={[mediaManagerStyles.contextMenuText, { color: theme.text.primary }]}>Rename</Text>
                            </TouchableOpacity>
                        )}

                        {/* Delete option - hide only for protected folders, allow file deletion in protected folders */}
                        {!(item.type === 'folder' && item.item.protected) && (
                            <TouchableOpacity
                                onPress={() => {
                                    if (item.type === 'folder') {
                                        setDeleteConfirmText('');
                                        setShowDeleteConfirm(item.id);
                                    } else {
                                        setShowDeleteFileModal(item);
                                    }
                                    setContextMenu(null);
                                }}
                                style={[mediaManagerStyles.contextMenuItemLast, mediaManagerStyles.contextMenuItem]}
                            >
                                <Ionicons name="trash" size={16} color="#e74c3c" />
                                <Text style={mediaManagerStyles.contextMenuTextDanger}>Delete</Text>
                            </TouchableOpacity>
                        )}
                    </View>
                </TouchableOpacity>
            </Modal>
        );
    };

    // First row: Breadcrumb and Search
    const renderBreadcrumbRow = () => {
        const isNarrow = Platform.OS === 'web' && typeof window !== 'undefined' && window.innerWidth <= 1024;
        return (
                <View style={[mediaManagerStyles.breadcrumbRow, isNarrow && mediaManagerStyles.breadcrumbRowMobile]}>
                    {/* Breadcrumb */}
                    <View style={mediaManagerStyles.breadcrumbContainer}>
                    {/* Back button */}
                    {folderPath.length > 0 && (
                        <TouchableOpacity
                            onPress={navigateBack}
                            style={mediaManagerStyles.backButton}
                        >
                            <Ionicons name="arrow-back" size={16} color={tx} />
                        </TouchableOpacity>
                    )}

                    {/* Breadcrumb path */}
                    <View style={mediaManagerStyles.breadcrumbPath}>
                        <TouchableOpacity onPress={() => navigateToBreadcrumb(-1)}>
                            <Text style={[mediaManagerStyles.breadcrumbRoot, { color: tx }]}>
                                {profileTabRootFolderId && folders[profileTabRootFolderId]?.name
                                    ? folders[profileTabRootFolderId].name
                                    : 'Root'}
                            </Text>
                        </TouchableOpacity>

                        {folderPath.map((pathItem, index) => (
                            <View key={pathItem.id} style={{ flexDirection: 'row', alignItems: 'center' }}>
                                <Text style={[mediaManagerStyles.breadcrumbSeparator, { color: tq }]}>/</Text>
                                <TouchableOpacity onPress={() => navigateToBreadcrumb(index)}>
                                    <Text style={[
                                        mediaManagerStyles.breadcrumbItem,
                                        index === folderPath.length - 1
                                            ? { color: tx, fontWeight: '700' }
                                            : { color: theme.text.link },
                                    ]}>{pathItem.name}</Text>
                                </TouchableOpacity>
                            </View>
                        ))}
                    </View>
                </View>

                    {/* Search Bar */}
                    <View style={[mediaManagerStyles.searchContainer, isNarrow && mediaManagerStyles.searchContainerFullWidth, { backgroundColor: theme.background.tertiary }]}>
                    <Ionicons name="search" size={16} color={tx} style={{ marginRight: 8 }} />
                    <TextInput
                        placeholder="Search files and folders..."
                        placeholderTextColor={tq}
                        value={searchQuery}
                        onChangeText={setSearchQuery}
                        style={[mediaManagerStyles.searchInput, { color: tx, backgroundColor: 'transparent' }]}
                    />
                    {searchQuery.length > 0 && (
                        <TouchableOpacity
                            onPress={() => setSearchQuery('')}
                            style={mediaManagerStyles.searchClearButton}
                        >
                            <Ionicons name="close-circle" size={16} color={tq} />
                        </TouchableOpacity>
                    )}
                </View>
            </View>
        );
    };

    // Second row: Action buttons
    const renderActionButtonsRow = () => {
        let currentIndex = null;
        if (selectedItem) {
            currentIndex = filteredItems.findIndex(item => item.id === selectedItem.id);
        }
        const canMoveUp = currentIndex > 0;
        const canMoveDown = currentIndex < filteredItems.length - 1;
        
        return (
                <View style={mediaManagerStyles.actionButtonsRow}>
                    {/* <TouchableOpacity
                        onPress={migrateExistingMedia}
                        disabled={loading}
                        style={[
                            mediaManagerStyles.actionButton,
                            mediaManagerStyles.migrateButton,
                            loading && mediaManagerStyles.actionButtonDisabled
                        ]}
                    >
                        <Ionicons name={loading ? "hourglass" : "cloud-upload"} size={16} color={tx} />
                        <Text style={[mediaManagerStyles.actionButtonText, { color: tx }]}>
                        {loading ? 'MIGRATING...' : 'MIGRATE MEDIA'}
                        </Text>
                    </TouchableOpacity> */}

                    {/* <TouchableOpacity
                        onPress={resetAllMediaData}
                        disabled={loading}
                        style={[
                            mediaManagerStyles.actionButton,
                            mediaManagerStyles.resetButton,
                            loading && mediaManagerStyles.actionButtonDisabled
                        ]}
                    >
                        <Ionicons name={loading ? "hourglass" : "trash"} size={16} color={tx} />
                        <Text style={[mediaManagerStyles.actionButtonText, { color: tx }]}>
                            {loading ? 'RESETTING...' : 'RESET ALL'}
                        </Text>
                    </TouchableOpacity>

                    <TouchableOpacity
                        onPress={setAllFilesUnprotected}
                        disabled={loading}
                        style={[
                            mediaManagerStyles.actionButton,
                            mediaManagerStyles.unprotectButton,
                            loading && mediaManagerStyles.actionButtonDisabled
                        ]}
                    >
                        <Text style={[mediaManagerStyles.actionButtonText, { color: tx }]}>
                            {loading ? 'UPDATING...' : 'UNPROTECT FILES'}
                        </Text>
                    </TouchableOpacity> */}

                {/* Open button */}
                    <TouchableOpacity
                        onPress={() => {
                            if (selectedItem && selectedItem.type === 'folder') {
                                navigateToFolder(selectedItem.id);
                            } 
                            setSelectedItem(null);
                        }}
                        style={mediaManagerStyles.openButton}
                    >
                        <Text style={mediaManagerStyles.actionButtonText}>OPEN</Text>
                    </TouchableOpacity>

                    {/* Move up button */}
                    <TouchableOpacity
                        onPress={() => {
                        if (selectedItem && canMoveUp) {
                                moveItem(selectedItem.id, selectedItem.type, 'up');
                                setSelectedItem(null);
                            }
                        }}
                        style={[
                            mediaManagerStyles.moveButton,
                            (!selectedItem || !canMoveUp) && mediaManagerStyles.moveButtonDisabled
                        ]}
                    >
                        <Ionicons name="chevron-up" size={16} color={tx} />
                    </TouchableOpacity>

                    {/* Move down button */}
                    <TouchableOpacity
                        onPress={() => {
                        if (selectedItem && canMoveDown) {
                                moveItem(selectedItem.id, selectedItem.type, 'down');
                                setSelectedItem(null);
                            }
                        }}
                        style={[
                            mediaManagerStyles.moveButton,
                            (!selectedItem || !canMoveDown) && mediaManagerStyles.moveButtonDisabled
                        ]}
                    >
                        <Ionicons name="chevron-down" size={16} color={tx} />
                    </TouchableOpacity>

                    {/* Clear selection */}
                    <TouchableOpacity
                        onPress={() => setSelectedItem(null)}
                        style={[
                            mediaManagerStyles.clearButton,
                            !selectedItem && mediaManagerStyles.clearButtonDisabled
                        ]}
                    >
                        <Ionicons name="close" size={16} color={tx} />
                    </TouchableOpacity>

                {/* Rename */}
                    <TouchableOpacity
                        onPress={() => {
                            if (selectedItem) {
                                const displayName = selectedItem.item?.name || '';
                                setRenameText(displayName);
                                setShowRenameModal({ id: selectedItem.id, type: selectedItem.type === 'folder' ? 'folder' : 'file', item: selectedItem.item });
                            }
                        }}
                        style={[
                            mediaManagerStyles.actionButton,
                            mediaManagerStyles.renameButton,
                            !selectedItem && mediaManagerStyles.actionButtonDisabled
                        ]}
                    >
                        <Ionicons name="create" size={14} color={theme.text.primary} />
                        <Text style={[mediaManagerStyles.actionButtonText, { color: theme.text.primary }]}>Rename</Text>
                    </TouchableOpacity>

                {/* Delete */}
                    <TouchableOpacity
                        onPress={() => {
                            if (selectedItem) {
                                setShowDeleteFileModal({ id: selectedItem.id, item: selectedItem.item });
                            }
                        }}
                        style={[
                            mediaManagerStyles.actionButton,
                            mediaManagerStyles.deleteButton,
                            !selectedItem && mediaManagerStyles.actionButtonDisabled
                        ]}
                    >
                        <Ionicons name="trash" size={14} color="#ffffff" />
                        <Text style={mediaManagerStyles.actionButtonText}>Delete</Text>
                    </TouchableOpacity>

                {/* New Folder / New Tour button */}
                    {!savedVenuesFlatView && (
                    <TouchableOpacity
                        onPress={() => {
                                setShowCreateFolder(true);
                        }}
                        style={[
                            mediaManagerStyles.actionButton,
                            mediaManagerStyles.newFolderButton
                        ]}
                    >
                        <Ionicons name="folder" size={16} color={tx} />
                        <Text style={[mediaManagerStyles.actionButtonText, { color: tx }]}>{openWithCreateFolder ? 'New Tour' : 'New Folder'}</Text>
                    </TouchableOpacity>
                    )}

                {/* Add Files button */}
                    {allowFileUpload && (
                    <TouchableOpacity
                        onPress={async () => {
                            let target = listFolderId;
                            if (!target && userData?.uid) {
                                target = await getOrCreateUserUploadsFolder(userData.uid);
                            }
                            if (target) uploadFileToFolder(target);
                        }}
                        disabled={loading}
                        style={[
                            mediaManagerStyles.addFilesButton,
                            loading && mediaManagerStyles.actionButtonDisabled
                        ]}
                    >
                        <Ionicons name={loading ? "hourglass" : "add"} size={14} color="#ffffff" />
                        <Text style={mediaManagerStyles.actionButtonText}>
                            {loading ? 'Adding...' : 'Add Files'}
                        </Text>
                    </TouchableOpacity>
                    )}
            </View>
        );
    };

    // Get current directory contents sorted by order
    const currentFolders = getCurrentFolders();
    const currentFiles = getCurrentFiles();

    // Combine folders and files, then sort by order
    const allItems = [
        ...currentFolders.map(([id, item]) => ({ id, item, type: 'folder' })),
        ...currentFiles.map(([id, item]) => ({ id, item, type: 'file' }))
    ].sort((a, b) => a.item.order - b.item.order);

    // Filter items based on search query
    const filteredItems = searchQuery.trim() 
        ? allItems.filter(({ id, item, type }) => {
            const searchTerm = searchQuery.toLowerCase();
            
            // Check if the item name matches
            if (item.name.toLowerCase().includes(searchTerm)) {
                return true;
            }
            
            // For folders, also check if any files inside match the search term
            if (type === 'folder') {
                const folderFiles = Object.entries(files).filter(([_, file]) => file.folderId === id);
                return folderFiles.some(([_, file]) => 
                    file.name.toLowerCase().includes(searchTerm)
                );
            }
            
            return false;
          })
        : allItems;

    // Function to move item up or down
    const moveItem = async (itemId, itemType, direction) => {
        const currentIndex = filteredItems.findIndex(item => item.id === itemId);
        if (currentIndex === -1) return;

        const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
        if (newIndex < 0 || newIndex >= filteredItems.length) return;

        try {
            // Create new array with swapped positions
            const newAllItems = [...allItems];
            const currentItem = filteredItems[currentIndex];
            const targetItem = filteredItems[newIndex];
            
            // Find the actual indices in allItems
            const currentActualIndex = allItems.findIndex(item => item.id === currentItem.id);
            const targetActualIndex = allItems.findIndex(item => item.id === targetItem.id);
            
            [newAllItems[currentActualIndex], newAllItems[targetActualIndex]] = [newAllItems[targetActualIndex], newAllItems[currentActualIndex]];

            // Update order values
            const updatedFolders = { ...folders };
            const updatedFiles = { ...files };

            newAllItems.forEach((item, index) => {
                if (item.type === 'folder') {
                    updatedFolders[item.id] = { ...item.item, order: index };
                } else {
                    updatedFiles[item.id] = { ...item.item, order: index };
                }
            });

            // Update database
            const userRef = doc(db, 'users', userData.uid);
            await persistUserLibrary(userRef, {
                folders: updatedFolders,
                files: updatedFiles
            });

            // Update local state
            setFolders(updatedFolders);
            setFiles(updatedFiles);
            setUser({ ...userData, folders: updatedFolders, files: updatedFiles });

        } catch (error) {
            console.error('Error updating order:', error);
            Alert.alert('Error', 'Failed to update item order');
        }
    };

    dropHandlerRef.current = (data) => {
        setIsDragOver(false);
        if (data.dragType === 'media') {
            if (!allowFileUploadRef.current) return;
            addMediaToMediaManager(data);
            return;
        }
        if (!allowProfileDropImportRef.current) return;
        if (data.dragType === 'venue') {
            addVenueToMediaManager(data);
        } else if (data.dragType === 'report') {
            addReportToMediaManager(data);
        }
    };

    return (
        <View style={{
            borderRadius: 0,
            overflow: 'hidden',
            borderWidth: 0,
            flex: 1,
            width: '100%',
            height: '100%',
        }}>
            <View 
                ref={mediaManagerRef}
                style={[
                    mediaManagerStyles.container,
                    { backgroundColor: 'transparent' },
                ]}
                className="media-manager-container"
                data-media-manager="true"
            >
                {/* Media Viewer */}
                <MediaViewer
                    visible={isMediaViewerVisible}
                    onClose={handleMediaViewerClose}
                    media={getCurrentFolderMedia()}
                    currentIndex={selectedMediaIndex}
                    onNavigate={setSelectedMediaIndex}
                />

                {/* Drag Over Indicator */}
                {isDragOver && (
                    <View style={mediaManagerStyles.dragOverIndicator}>
                        <View style={mediaManagerStyles.dragOverContent}>
                            <Ionicons name="cloud-upload" size={24} color={tx} />
                            <Text style={[mediaManagerStyles.dragOverText, { color: tx }]}>
                                Drop here to add to Media Manager
                            </Text>
                        </View>
                    </View>
                )}

                {/* Loading Overlay */}
                {loading && (
                    <View style={mediaManagerStyles.loadingOverlay}>
                        <View style={[mediaManagerStyles.loadingContent, { backgroundColor: theme.background.tertiary }]}>
                            <Ionicons name="hourglass" size={22} color={tx} />
                            <Text style={[mediaManagerStyles.loadingText, { color: tx }]}>
                                Adding to Media Manager...
                            </Text>
                        </View>
                    </View>
                )}

                {/* Header - Two Rows */}
                {renderBreadcrumbRow()}
                {renderActionButtonsRow()}

                {/* Folder Selection Mode - Show button to select current folder */}
                {onFolderSelect && (venueToSave || communityPostToSave || communityProfileToSave) && !openWithCreateFolder && (
                  <View style={{
                    padding: getResponsiveValue({ mobile: 10, tablet: 10, laptop: 15, desktop: 15 }),
                    backgroundColor: 'transparent',
                    borderTopWidth: 1,
                    borderTopColor: theme.ui.border,
                    borderBottomWidth: 1,
                    borderBottomColor: theme.ui.border
                  }}>
                    <TouchableOpacity
                      onPress={async () => {
                        if (!listFolderId || openWithCreateFolder) {
                          return;
                        }
                        if (venueToSave) {
                          await saveVenueToFolder(listFolderId, venueToSave);
                        } else if (communityPostToSave) {
                          await saveCommunityPostToFolder(listFolderId, communityPostToSave);
                        } else if (communityProfileToSave) {
                          await saveCommunityProfileToFolder(listFolderId, communityProfileToSave);
                        }
                        onFolderSelect(listFolderId);
                      }}
                      style={{
                        backgroundColor: openWithCreateFolder ? '#3a3a3a' : '#4e7de0',
                        padding: 15,
                        borderRadius: 8,
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                      disabled={!listFolderId}
                    >
                      <Text style={{
                        color: (listFolderId && !openWithCreateFolder) ? tx : tq,
                        fontSize: 16,
                        fontFamily: 'VastagoGrotesk',
                        fontWeight: 'bold'
                      }}>
                        {listFolderId
                          ? `Save to "${folders[listFolderId]?.name || 'Current Folder'}"`
                          : (venueToSave ? 'Select Folder to Save Venue' : (communityProfileToSave ? 'Select Folder to Save Profile' : 'Select Folder to Save Post'))}
                      </Text>
                    </TouchableOpacity>
                    {listFolderId && (
                      <TouchableOpacity
                        onPress={() => navigateBack()}
                        style={{
                          marginTop: 10,
                          padding: 10,
                          alignItems: 'center'
                        }}
                      >
                        <Text style={{
                          color: tq,
                          fontSize: 14,
                          fontFamily: 'VastagoGrotesk'
                        }}>
                          Or navigate to a different folder
                        </Text>
                      </TouchableOpacity>
                    )}
                  </View>
                )}

                {/* Current Directory Contents */}
                <View style={[mediaManagerStyles.contentArea, (Platform.OS !== 'web' || (typeof window !== 'undefined' && window.innerWidth <= 820)) && mediaManagerStyles.contentAreaSingleColumn]}>
                    {/* Render filtered items sorted by order */}
                    {filteredItems.map(({ id, item, type }, index) =>
                        type === 'folder'
                            ? renderFolder(id, item, index)
                            : renderItem(id, item, index)
                    )}
                    {/* Empty state */}
                    {filteredItems.length === 0 && (
                        <View style={mediaManagerStyles.emptyState}>
                            <Ionicons name={listFolderId ? "images-outline" : "folder-outline"} size={48} color={tx} />
                            <Text style={[mediaManagerStyles.emptyStateText, { color: tx }]}>
                                {searchQuery.trim() 
                                    ? `No files or folders found matching "${searchQuery}"`
                                    : listFolderId
                                    ? "This folder is empty. Add some files or create subfolders!"
                                    : "No folders yet. Create your first folder to organize your media!"
                                }
                            </Text>
                        </View>
                    )}
                </View>


                {/* Create Folder / Create Tour Modal */}
                <Modal
                    visible={showCreateFolder}
                    transparent={true}
                    animationType="fade"
                    onRequestClose={() => setShowCreateFolder(false)}
                >
                    <View style={mediaManagerStyles.modalOverlay}>
                        <View style={mediaManagerStyles.modalContent}>
                            <Text style={mediaManagerStyles.modalTitle}>{openWithCreateFolder ? 'Create New Tour' : 'Create New Folder'}</Text>

                            <TextInput
                                placeholder={openWithCreateFolder ? 'Tour name' : 'Folder name'}
                                value={newFolder.name}
                                onChangeText={(text) => setNewFolder({ ...newFolder, name: text })}
                                style={mediaManagerStyles.modalInput}
                            />

                            <Text style={mediaManagerStyles.modalLabel}>Choose folder color:</Text>

                            <View style={mediaManagerStyles.colorPickerContainer}>
                                {folderColors.map((color) => (
                                    <TouchableOpacity
                                        key={color}
                                        onPress={() => setNewFolder({ ...newFolder, color })}
                                        style={[
                                            mediaManagerStyles.colorPickerItem,
                                            newFolder.color === color && mediaManagerStyles.colorPickerItemSelected,
                                            { backgroundColor: color },
                                        ]}
                                    />
                                ))}
                            </View>

                            <View style={mediaManagerStyles.modalButtonRow}>
                                <TouchableOpacity
                                    onPress={() => setShowCreateFolder(false)}
                                    style={[mediaManagerStyles.modalButton, mediaManagerStyles.modalButtonCancel]}
                                >
                                    <Text style={mediaManagerStyles.modalButtonText}>Cancel</Text>
                                </TouchableOpacity>

                                <TouchableOpacity
                                    onPress={createFolder}
                                    style={[mediaManagerStyles.modalButton, mediaManagerStyles.modalButtonPrimary]}
                                >
                                    <Text style={[mediaManagerStyles.modalButtonText, mediaManagerStyles.modalButtonTextPrimary, { color: tx }]}>Create</Text>
                                </TouchableOpacity>
                            </View>
                        </View>
                    </View>
                </Modal>

                {/* Delete Confirmation Modal */}
                <Modal
                    visible={showDeleteConfirm !== null}
                    transparent={true}
                    animationType="fade"
                    onRequestClose={() => {
                        setShowDeleteConfirm(null);
                        setDeleteConfirmText('');
                    }}
                >
                    <View style={mediaManagerStyles.modalOverlay}>
                        <View style={mediaManagerStyles.modalContent}>
                            <Text style={[mediaManagerStyles.modalTitle, { color: '#e74c3c' }]}>Delete Folder</Text>

                            {showDeleteConfirm && (() => {
                                const { foldersToDelete, filesToDelete } = getAllNestedItems(showDeleteConfirm);
                                return (
                                    <View>
                                        <Text style={[mediaManagerStyles.modalLabel, { textAlign: 'center' }]}>
                                            This will permanently delete the folder "{folders[showDeleteConfirm]?.name}" and ALL its contents:
                                        </Text>
                                        
                                        <View style={mediaManagerStyles.deleteWarning}>
                                            <Text style={mediaManagerStyles.deleteWarningTitle}>⚠️ Items to be deleted:</Text>
                                            
                                            <Text style={mediaManagerStyles.deleteWarningText}>
                                                📁 {foldersToDelete.length} folder{foldersToDelete.length !== 1 ? 's' : ''} (including subfolders)
                                            </Text>
                                            
                                            <Text style={mediaManagerStyles.deleteWarningTextLast}>
                                                📄 {filesToDelete.length} file{filesToDelete.length !== 1 ? 's' : ''}
                                            </Text>
                                        </View>
                                        
                                        <Text style={mediaManagerStyles.deleteConfirmText}>
                                            This action cannot be undone!
                                        </Text>
                                    </View>
                                );
                            })()}

                            <Text style={mediaManagerStyles.modalLabel}>Type the folder name to confirm:</Text>

                            <TextInput
                                placeholder="Enter folder name"
                                placeholderTextColor="#DDDDDD"
                                value={deleteConfirmText}
                                onChangeText={setDeleteConfirmText}
                                style={mediaManagerStyles.modalInput}
                            />

                            <View style={mediaManagerStyles.modalButtonRow}>
                                <TouchableOpacity
                                    onPress={() => {
                                        setShowDeleteConfirm(null);
                                        setDeleteConfirmText('');
                                    }}
                                    style={[mediaManagerStyles.modalButton, mediaManagerStyles.modalButtonCancel]}
                                >
                                    <Text style={mediaManagerStyles.modalButtonText}>Cancel</Text>
                                </TouchableOpacity>

                                <TouchableOpacity
                                    onPress={() => deleteFolder(showDeleteConfirm)}
                                    style={[mediaManagerStyles.modalButton, { backgroundColor: '#e74c3c' }]}
                                >
                                    <Text style={[mediaManagerStyles.modalButtonText, { color: tx, fontFamily: 'Glacial Indifference' }]}>Delete</Text>
                                </TouchableOpacity>
                            </View>
                        </View>
                    </View>
                </Modal>

                {/* Rename Modal */}
                <Modal
                    visible={showRenameModal !== null}
                    transparent={true}
                    animationType="fade"
                    onRequestClose={() => {
                        setShowRenameModal(null);
                        setRenameText('');
                    }}
                >
                    <View style={mediaManagerStyles.modalOverlay}>
                        <View style={mediaManagerStyles.modalContent}>
                            <Text style={mediaManagerStyles.modalTitle}>
                                Rename {showRenameModal?.type === 'folder' ? 'Folder' : 'File'}
                            </Text>

                            <TextInput
                                placeholder="Enter new name"
                                value={renameText}
                                onChangeText={setRenameText}
                                style={mediaManagerStyles.modalInput}
                                autoFocus={true}
                                selectTextOnFocus={true}
                            />

                            <View style={mediaManagerStyles.modalButtonRow}>
                                <TouchableOpacity
                                    onPress={() => {
                                        setShowRenameModal(null);
                                        setRenameText('');
                                    }}
                                    style={[mediaManagerStyles.modalButton, mediaManagerStyles.modalButtonCancel]}
                                >
                                    <Text style={mediaManagerStyles.modalButtonText}>Cancel</Text>
                                </TouchableOpacity>

                                <TouchableOpacity
                                    onPress={() => {
                                        if (showRenameModal) {
                                            renameItem(showRenameModal.id, showRenameModal.type, renameText);
                                        }
                                    }}
                                    style={[mediaManagerStyles.modalButton, mediaManagerStyles.modalButtonPrimary]}
                                >
                                    <Text style={[mediaManagerStyles.modalButtonText, mediaManagerStyles.modalButtonTextPrimary, { color: tx }]}>Rename</Text>
                                </TouchableOpacity>
                            </View>
                        </View>
                    </View>
                </Modal>

                {/* Color Picker Modal */}
                <Modal
                    visible={showColorModal !== null}
                    transparent={true}
                    animationType="fade"
                    onRequestClose={() => {
                        setShowColorModal(null);
                        setSelectedColor('#0bb5df');
                    }}
                >
                    <View style={mediaManagerStyles.modalOverlay}>
                        <View style={mediaManagerStyles.modalContent}>
                            <Text style={mediaManagerStyles.modalTitle}>
                                Change Folder Color
                            </Text>

                            <Text style={[mediaManagerStyles.modalLabel, { textAlign: 'center' }]}>
                                Choose a new color for "{showColorModal?.item?.name}"
                            </Text>

                            <View style={[mediaManagerStyles.colorPickerContainer, { justifyContent: 'center', marginBottom: 25 }]}>
                                {folderColors.map((color) => (
                                    <TouchableOpacity
                                        key={color}
                                        onPress={() => setSelectedColor(color)}
                                        style={[
                                            mediaManagerStyles.colorPickerItemSmall,
                                            selectedColor === color && mediaManagerStyles.colorPickerItemSelected
                                        ]}
                                    />
                                ))}
                            </View>

                            <View style={mediaManagerStyles.modalButtonRow}>
                                <TouchableOpacity
                                    onPress={() => {
                                        setShowColorModal(null);
                                        setSelectedColor('#0bb5df');
                                    }}
                                    style={[mediaManagerStyles.modalButton, mediaManagerStyles.modalButtonCancel]}
                                >
                                    <Text style={mediaManagerStyles.modalButtonText}>Cancel</Text>
                                </TouchableOpacity>

                                <TouchableOpacity
                                    onPress={() => {
                                        if (showColorModal) {
                                            changeFolderColor(showColorModal.id, selectedColor);
                                        }
                                    }}
                                    style={[mediaManagerStyles.modalButton, { backgroundColor: selectedColor }]}
                                >
                                    <Text style={{ 
                                        color: tx, 
                                        fontSize: 16, 
                                        fontFamily: 'Glacial Indifference',
                                        textShadowColor: 'rgba(0,0,0,0.3)',
                                        textShadowOffset: { width: 1, height: 1 },
                                        textShadowRadius: 2
                                    }}>Change Color</Text>
                                </TouchableOpacity>
                            </View>
                        </View>
                    </View>
                </Modal>

                {/* Delete File Modal */}
                <Modal
                    visible={showDeleteFileModal !== null}
                    transparent={true}
                    animationType="fade"
                    onRequestClose={() => setShowDeleteFileModal(null)}
                >
                    <View style={mediaManagerStyles.modalOverlay}>
                        <View style={[mediaManagerStyles.modalContent, { alignItems: 'center' }]}>
                            <View style={mediaManagerStyles.deleteFileIcon}>
                                <Ionicons name="trash" size={48} color="#e74c3c" />
                                <Text style={mediaManagerStyles.deleteFileTitle}>Delete File</Text>
                            </View>

                            <Text style={mediaManagerStyles.deleteFileDescription}>
                                Are you sure you want to delete this file?
                            </Text>

                            <View style={mediaManagerStyles.deleteFileInfo}>
                                <Text style={mediaManagerStyles.deleteFileInfoTitle}>"{showDeleteFileModal?.item?.name}"</Text>
                                {showDeleteFileModal?.item?.migratedFrom && (
                                    <Text style={mediaManagerStyles.deleteFileInfoSubtext}>
                                        Source: {showDeleteFileModal.item.migratedFrom}
                                    </Text>
                                )}
                                {showDeleteFileModal?.item?.uploadSource && (
                                    <Text style={mediaManagerStyles.deleteFileInfoSubtext}>
                                        Uploaded from: {showDeleteFileModal.item.uploadSource}
                                    </Text>
                                )}
                            </View>

                            <Text style={mediaManagerStyles.deleteFileWarning}>
                                This action cannot be undone. The file will be removed from your folder system.
                            </Text>

                            <View style={mediaManagerStyles.modalButtonRow}>
                                <TouchableOpacity
                                    onPress={() => setShowDeleteFileModal(null)}
                                    style={[mediaManagerStyles.modalButton, mediaManagerStyles.modalButtonCancel]}
                                >
                                    <Text style={[mediaManagerStyles.modalButtonText, { fontFamily: 'Glacial Indifference', color: '#333' }]}>Cancel</Text>
                                </TouchableOpacity>

                                <TouchableOpacity
                                    onPress={() => {
                                        if (showDeleteFileModal) {
                                            deleteFile(showDeleteFileModal.id);
                                            setShowDeleteFileModal(null);
                                        }
                                    }}
                                    style={[mediaManagerStyles.modalButton, { backgroundColor: '#e74c3c' }]}
                                >
                                    <Text style={[mediaManagerStyles.modalButtonText, { color: tx, fontFamily: 'Glacial Indifference', fontWeight: 'bold' }]}>Delete</Text>
                                </TouchableOpacity>
                            </View>
                        </View>
                    </View>
                </Modal>

                {/* Success Modal */}
                <Modal
                    visible={showSuccessModal !== null}
                    transparent={true}
                    animationType="fade"
                    onRequestClose={() => setShowSuccessModal(null)}
                >
                    <View style={mediaManagerStyles.modalOverlay}>
                        <View style={[mediaManagerStyles.modalContent, mediaManagerStyles.successModal]}>
                            <Ionicons name="checkmark-circle" size={48} color="#2ecc71" />
                            <Text style={mediaManagerStyles.successTitle}>Success!</Text>

                            <Text style={mediaManagerStyles.successMessage}>{showSuccessModal}</Text>

                            <TouchableOpacity
                                onPress={() => setShowSuccessModal(null)}
                                style={mediaManagerStyles.successButton}
                            >
                                <Text style={[mediaManagerStyles.successButtonText, { color: tx }]}>OK</Text>
                            </TouchableOpacity>
                        </View>
                    </View>
                </Modal>

                {/* Error Modal */}
                <Modal
                    visible={showErrorModal !== null}
                    transparent={true}
                    animationType="fade"
                    onRequestClose={() => setShowErrorModal(null)}
                >
                    <View style={mediaManagerStyles.modalOverlay}>
                        <View style={[mediaManagerStyles.modalContent, mediaManagerStyles.errorModal]}>
                            <Ionicons name="alert-circle" size={48} color="#e74c3c" />
                            <Text style={mediaManagerStyles.errorTitle}>Error</Text>

                            <Text style={mediaManagerStyles.errorMessage}>{showErrorModal}</Text>

                            <TouchableOpacity
                                onPress={() => setShowErrorModal(null)}
                                style={mediaManagerStyles.errorButton}
                            >
                                <Text style={[mediaManagerStyles.errorButtonText, { color: tx }]}>OK</Text>
                            </TouchableOpacity>
                        </View>
                    </View>
                </Modal>

                {/* Venue Notes Modal */}
                <Modal
                    visible={notesModalVenue !== null}
                    transparent={true}
                    animationType="fade"
                    onRequestClose={handleCloseNotes}
                >
                    <View style={mediaManagerStyles.modalOverlay}>
                        <View style={[mediaManagerStyles.modalContent, { maxWidth: 400 }]}>
                            <Text style={[mediaManagerStyles.modalTitle, { marginBottom: 8 }]}>
                                Notes – {notesModalVenue?.name}
                            </Text>
                            <TextInput
                                style={{ borderWidth: 1, borderColor: '#ddd', borderRadius: 8, padding: 12, minHeight: 100, textAlignVertical: 'top', marginBottom: 12 }}
                                placeholder="Add notes for this venue..."
                                placeholderTextColor="#888"
                                value={notesText}
                                onChangeText={setNotesText}
                                multiline
                            />
                            <View style={{ flexDirection: 'row', gap: 12, justifyContent: 'flex-end' }}>
                                <TouchableOpacity onPress={handleCloseNotes} style={mediaManagerStyles.errorButton}>
                                    <Text style={[mediaManagerStyles.errorButtonText, { color: tx }]}>Cancel</Text>
                                </TouchableOpacity>
                                <TouchableOpacity onPress={handleSaveNotes} style={mediaManagerStyles.successButton} disabled={notesSaving}>
                                    <Text style={[mediaManagerStyles.successButtonText, { color: tx }]}>{notesSaving ? 'Saving...' : 'Save'}</Text>
                                </TouchableOpacity>
                            </View>
                        </View>
                    </View>
                </Modal>

                {/* Room EQ Viewer - uses same EQ/RF/Add layout as VenueScreen */}
                <RoomEqViewerModal
                    visible={eqViewerVisible}
                    submissions={eqViewerSubmissions}
                    initialIndex={eqViewerIndex}
                    onClose={handleCloseEq}
                    onDeleteSubmission={handleDeleteEqSubmission}
                    onEditSubmission={handleEditRoomEqSubmission}
                    onGoToVenue={handleGoToVenueFromEqModal}
                />

                {/* Community Post View Modal — scrollable; message poster like Community List */}
                <Modal
                    visible={showCommunityPostModal && communityPostModalData !== null}
                    transparent={true}
                    animationType="fade"
                    onRequestClose={() => { setShowCommunityPostModal(false); setCommunityPostModalData(null); }}
                >
                    <View style={mediaManagerStyles.modalOverlay}>
                        <ScrollView
                            style={{ width: '100%', maxWidth: 440, maxHeight: Platform.OS === 'web' ? '90vh' : '90%' }}
                            contentContainerStyle={{ padding: 12, paddingBottom: 28, alignItems: 'stretch' }}
                            keyboardShouldPersistTaps="handled"
                            showsVerticalScrollIndicator
                        >
                            <View style={mediaManagerStyles.postCard}>
                                <View style={mediaManagerStyles.postCardHeader}>
                                    <View style={mediaManagerStyles.postCardHeaderInputs}>
                                        {communityPostModalData?.postContent ? (
                                            <Text style={mediaManagerStyles.postCardPostInput}>{communityPostModalData.postContent}</Text>
                                        ) : null}
                                        {communityPostModalData?.location ? (
                                            <Text style={mediaManagerStyles.postCardLocationInput}>{communityPostModalData.location}</Text>
                                        ) : null}
                                        {!communityPostModalData?.postContent && !communityPostModalData?.location ? (
                                            <Text style={mediaManagerStyles.postCardPostInput} numberOfLines={1}>Post</Text>
                                        ) : null}
                                    </View>
                                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
                                        <TouchableOpacity
                                            style={mediaManagerStyles.postCardCloseBtn}
                                            onPress={handleCommunityPostModalMessage}
                                            accessibilityLabel="Message poster"
                                        >
                                            <Image
                                                source={require('../../assets/icons/paper-plane.png')}
                                                style={{ width: 22, height: 22 }}
                                                resizeMode="contain"
                                            />
                                        </TouchableOpacity>
                                        <TouchableOpacity
                                            style={mediaManagerStyles.postCardCloseBtn}
                                            onPress={() => { setShowCommunityPostModal(false); setCommunityPostModalData(null); }}
                                            accessibilityLabel="Close"
                                        >
                                            <Ionicons name="close" size={28} color="#5c4a32" />
                                        </TouchableOpacity>
                                    </View>
                                </View>
                                {communityPostModalData?.items && communityPostModalData.items.length > 0 ? (
                                    <PostGridDisplay items={communityPostModalData.items} suppressEmptyWidgetHints />
                                ) : (
                                    <Text style={{ fontFamily: 'VastagoGrotesk', fontSize: 13, color: '#999', alignSelf: 'center' }}>No grid content</Text>
                                )}
                            </View>
                        </ScrollView>
                    </View>
                </Modal>

                <Modal
                    visible={showCommunityProfileModal && communityProfileModalData !== null}
                    transparent={true}
                    animationType="fade"
                    onRequestClose={() => { setShowCommunityProfileModal(false); setCommunityProfileModalData(null); }}
                >
                    <View style={mediaManagerStyles.modalOverlay}>
                        <View style={[mediaManagerStyles.modalContent, { maxWidth: 420, width: '92%', alignItems: 'center' }]}>
                            {communityProfileModalData?.photoURL ? (
                                <Image
                                    source={{ uri: communityProfileModalData.photoURL }}
                                    style={{ width: 92, height: 92, borderRadius: 46, marginBottom: 12 }}
                                />
                            ) : (
                                <View style={{ width: 92, height: 92, borderRadius: 46, marginBottom: 12, backgroundColor: '#d9c8a5', alignItems: 'center', justifyContent: 'center' }}>
                                    <Text style={{ fontFamily: 'VastagoGrotesk', fontSize: 32, color: '#4b3b1f' }}>
                                        {((communityProfileModalData?.savedName || communityProfileModalData?.username || communityProfileModalData?.title || 'P').charAt(0) || 'P').toUpperCase()}
                                    </Text>
                                </View>
                            )}
                            <Text style={[mediaManagerStyles.modalTitle, { textAlign: 'center' }]}>
                                {communityProfileModalData?.savedName || communityProfileModalData?.username || 'Community profile'}
                            </Text>
                            {communityProfileModalData?.title ? (
                                <Text style={[mediaManagerStyles.modalLabel, { textAlign: 'center' }]}>{communityProfileModalData.title}</Text>
                            ) : null}
                            {communityProfileModalData?.location ? (
                                <Text style={[mediaManagerStyles.modalLabel, { textAlign: 'center' }]}>{communityProfileModalData.location}</Text>
                            ) : null}
                            {communityProfileModalData?.bio ? (
                                <Text style={[mediaManagerStyles.modalLabel, { textAlign: 'center', marginTop: 10 }]} numberOfLines={4}>
                                    {communityProfileModalData.bio}
                                </Text>
                            ) : null}
                            <View style={[mediaManagerStyles.modalButtonRow, { marginTop: 16, width: '100%' }]}>
                                <TouchableOpacity
                                    onPress={() => { setShowCommunityProfileModal(false); setCommunityProfileModalData(null); }}
                                    style={[mediaManagerStyles.modalButton, mediaManagerStyles.modalButtonCancel]}
                                >
                                    <Text style={mediaManagerStyles.modalButtonText}>Close</Text>
                                </TouchableOpacity>
                                <TouchableOpacity
                                    onPress={handleOpenCommunityProfile}
                                    style={[mediaManagerStyles.modalButton, mediaManagerStyles.modalButtonPrimary]}
                                >
                                    <Text style={[mediaManagerStyles.modalButtonText, mediaManagerStyles.modalButtonTextPrimary, { color: tx }]}>
                                        Open Profile
                                    </Text>
                                </TouchableOpacity>
                            </View>
                        </View>
                    </View>
                </Modal>

                {/* Context Menu */}
                {renderContextMenu()}

            </View>
        </View>

    );
};

export default MediaManager;
